// ---------------------------------------------------------------------------
// RBAC Catalog (Single source of truth)
// ---------------------------------------------------------------------------
// Mirrors the horizontal navbar (Sidebar.jsx -> navItems). RBAC-gated modules
// live here so Screen Access, runtime gates, and the backend stay aligned.
// Home (/admin/home) is omitted — it is available to every signed-in admin user
// and is not configurable per role.
//
// Screen Access contract (User Role Management → Step 3):
// - Modules render in this file’s module array order (top-nav flow).
// - Screen order + section labels come from SCREEN_ACCESS_FLOW (all modules).
// - Optional per-screen `flowGroup` overrides the flow map entry.
// - Descriptions are catalog metadata only — Screen Access does not show them.
// - Add a screen under module.screens AND (recommended) SCREEN_ACCESS_FLOW so
//   it sorts/groups correctly. If omitted from the flow map it still appears
//   at the end of that module. UserRoleManagement loads via getScreenCatalogForRole().
//
// Shape stored per role:
//   screenAccess = {
//     <moduleKey>: {
//       view:    boolean,           // controls navbar tab visibility
//       create:  boolean,           // module-level action flags (optional)
//       edit:    boolean,           // kept for requirePermission() middleware
//       delete:  boolean,
//       approve: boolean,
//       export:  boolean,
//       screens: {
//         <screenKey>: { view, create, edit, delete, approve, export }
//       }
//     }
//   }
// ---------------------------------------------------------------------------

export const ACTIONS = ["view", "create", "import", "edit", "delete", "approve", "export"];

/**
 * Stable UI order for the Screen Access wizard: one row per screen, actions
 * rendered left-to-right in this order (filtered by each screen’s `actions`).
 */
export const SCREEN_ACCESS_ACTION_ORDER = [
  "view",
  "create",
  "import",
  "edit",
  "approve",
  "export",
  "delete",
];

/**
 * Coarse verb groupings (metadata / docs). The Screen Access wizard renders
 * actions in a single flat row per screen using `SCREEN_ACCESS_ACTION_ORDER`.
 */
export const SCREEN_ACCESS_ACTION_GROUPS = [
  {
    id: "access",
    title: "Access",
    description:
      "Allow opening this screen for this role. Turn off to hide it. Other options below only apply when this is on.",
    actions: ["view"],
  },
  {
    id: "create_import",
    title: "Create / import",
    description:
      "Adds new rows or runs an import/upload where this screen supports it.",
    actions: ["create", "import"],
  },
  {
    id: "modify",
    title: "Modify",
    description:
      "Edit saved details or approve steps like leave or payroll—depends on this screen.",
    actions: ["edit", "approve"],
  },
  {
    id: "export",
    title: "Export",
    description: "Download spreadsheets or PDF-style outputs where available.",
    actions: ["export"],
  },
  {
    id: "deactivate",
    title: "Deactivate / remove",
    description:
      "Deactivate, delete, or close items—the checkbox label describes exactly what.",
    actions: ["delete"],
  },
];

/**
 * UX rule (stored shape unchanged):
 * - Turning **Edit** (or any mutator) **on** while View is off → View is turned on.
 * - Turning **View off** clears every mutator on that screen (Edit, etc. follow View off).
 * - `preferViewOff`: set when the user just unchecked View so we do not re‑enable View
 *   while Edit is still true in the same update payload.
 *
 * @param {object} [options]
 * @param {boolean} [options.preferViewOff]  User toggled View → false this update.
 */
export function normalizeScreenPermissionRow(
  screenPerms = {},
  declaredActions = [],
  options = {}
) {
  const declared = new Set(declaredActions);
  const row = { ...screenPerms };
  for (const action of ACTIONS) {
    if (!declared.has(action)) row[action] = false;
  }
  const mutators = SCREEN_ACCESS_ACTION_ORDER.filter(
    (a) => a !== "view" && declared.has(a)
  );

  if (options.preferViewOff && declared.has("view") && row.view === false) {
    mutators.forEach((a) => {
      row[a] = false;
    });
    return row;
  }

  const anyMutatorOn = mutators.some((a) => !!row[a]);
  if (anyMutatorOn) {
    row.view = true;
  }
  if (!row.view) {
    mutators.forEach((a) => {
      row[a] = false;
    });
  }
  return row;
}

/** Module-level flags = OR across screens (only where that screen declares the action). */
export function syncModuleFlagsFromScreens(moduleAcc, moduleDef) {
  if (!moduleDef?.screens?.length) return;
  const screens = moduleAcc.screens || {};
  for (const action of ACTIONS) {
    const anyScreen = moduleDef.screens.some(
      (s) =>
        Array.isArray(s.actions) &&
        s.actions.includes(action) &&
        !!(screens[s.key]?.[action])
    );
    moduleAcc[action] = anyScreen;
  }
}
export const OPERATION_CAPABILITIES = [
  "view",
  "create",
  "edit",
  "update",
  "delete",
  "approve",
  "import",
  "export",
];

const toTitleCase = (value = "") =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveCapabilities = (screen = {}) => {
  const actionSet = new Set(Array.isArray(screen.actions) ? screen.actions : []);
  const labelKey = `${screen.key || ""} ${screen.label || ""}`.toLowerCase();
  const importScreenKeys = new Set([
    "employee_list",
    "attendance_import",
    "comp_off_opening_import",
    "attendance_lop_reversal_import",
  ]);
  if (actionSet.has("edit")) actionSet.add("update");
  if (labelKey.includes("import") || importScreenKeys.has(screen.key)) {
    actionSet.add("import");
  }
  const prioritized = OPERATION_CAPABILITIES.filter((op) => actionSet.has(op));
  return prioritized;
};

// Ensures every screen has a labelled, title-cased entry for each declared
// action. Screens may ship a partial `actionLabels` map (preferred UX, e.g.
// "Save Changes" instead of "Edit"); missing keys fall back to Title Case.
const buildActionLabels = (screenDef = {}) => {
  const out = {};
  const supplied = screenDef.actionLabels || {};
  const actions = Array.isArray(screenDef.actions) ? screenDef.actions : [];
  for (const action of actions) {
    const value = supplied[action];
    out[action] = value && String(value).trim() ? String(value) : toTitleCase(action);
  }
  return out;
};

/** Short UI hints for Screen Access wizard only (e.g. button vs table row). Keys must match declared actions. */
const normalizeActionWhere = (raw, declaredActions = []) => {
  if (!raw || typeof raw !== "object") return undefined;
  const allowed = new Set(declaredActions);
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    if (!allowed.has(key)) continue;
    const s = String(val ?? "").trim();
    if (s) out[key] = s;
  }
  return Object.keys(out).length ? out : undefined;
};

/**
 * Flow-wise Screen Access order + grouping for every admin module.
 * Array order = display order. `flowGroup` = section label in Screen Access.
 * Screens present in catalog but missing here are appended automatically
 * (so new screens still load without editing this map — add them here for
 * correct placement/group when you create them).
 */
export const SCREEN_ACCESS_FLOW = {
  workspaces: [
    { key: "reports_workspace", flowGroup: "Hubs" },
    { key: "import_export_workspace", flowGroup: "Hubs" },
    { key: "checklist_workspace", flowGroup: "Hubs" },
    { key: "settings_workspace", flowGroup: "Hubs" },
    { key: "activity_log_monitor", flowGroup: "Settings tools" },
    { key: "notification_emails", flowGroup: "Settings tools" },
  ],
  configuration: [
    { key: "enterprise", flowGroup: "Setup" },
    { key: "masters", flowGroup: "Setup" },
    { key: "settings", flowGroup: "Setup" },
    { key: "config_set_product_theme", flowGroup: "Setup" },
    { key: "config_set_org_preferences", flowGroup: "Setup" },
    { key: "masters_import", flowGroup: "Import" },
    { key: "attendance_codes", flowGroup: "Legacy" },
    { key: "activity_console", flowGroup: "Legacy" },
  ],
  employee: [
    { key: "employee_dashboard", flowGroup: "Dashboard" },
    { key: "onboard_employee", flowGroup: "Employee" },
    { key: "employee_list", flowGroup: "Employee" },
    { key: "create_employee", flowGroup: "Employee" },
    { key: "employee_profile", flowGroup: "Employee" },
    { key: "org_details", flowGroup: "Employee" },
    { key: "personal_details", flowGroup: "Employee" },
    { key: "family_details", flowGroup: "Employee" },
    { key: "bank_details", flowGroup: "Employee" },
    { key: "education_details", flowGroup: "Employee" },
    { key: "profession_details", flowGroup: "Employee" },
    { key: "statutory_details", flowGroup: "Employee" },
    { key: "profile_documents", flowGroup: "Employee" },
    { key: "edit_employee", flowGroup: "Legacy" },
    { key: "employee_documents", flowGroup: "Legacy" },
    { key: "emp_act_view_employee", flowGroup: "Actions" },
    { key: "emp_act_generate_letter", flowGroup: "Actions" },
    { key: "emp_act_confirmation", flowGroup: "Actions" },
    { key: "emp_act_transfer", flowGroup: "Actions" },
    { key: "emp_act_resignation", flowGroup: "Actions" },
    { key: "emp_action_hub", flowGroup: "Action Hub" },
    { key: "employee_import", flowGroup: "Import & Export" },
    { key: "emp_imp_org", flowGroup: "Import & Export" },
    { key: "emp_imp_personal", flowGroup: "Import & Export" },
    { key: "emp_imp_family", flowGroup: "Import & Export" },
    { key: "emp_imp_bank", flowGroup: "Import & Export" },
    { key: "emp_imp_qualification", flowGroup: "Import & Export" },
    { key: "emp_imp_professional", flowGroup: "Import & Export" },
    { key: "emp_imp_statutory", flowGroup: "Import & Export" },
    { key: "employee_bulk_exit", flowGroup: "Import & Export" },
    { key: "employee_export", flowGroup: "Import & Export" },
    { key: "emp_exp_org", flowGroup: "Import & Export" },
    { key: "emp_exp_personal", flowGroup: "Import & Export" },
    { key: "emp_exp_family", flowGroup: "Import & Export" },
    { key: "emp_exp_bank", flowGroup: "Import & Export" },
    { key: "emp_exp_educational", flowGroup: "Import & Export" },
    { key: "emp_exp_professional", flowGroup: "Import & Export" },
    { key: "emp_exp_statutory", flowGroup: "Import & Export" },
    { key: "emp_exp_all", flowGroup: "Import & Export" },
    { key: "employee_reports", flowGroup: "Reports" },
    { key: "emp_rpt_employees", flowGroup: "Reports" },
    { key: "emp_rpt_age", flowGroup: "Reports" },
    { key: "emp_rpt_service", flowGroup: "Reports" },
    { key: "emp_rpt_salary", flowGroup: "Reports" },
    { key: "emp_rpt_confirmation", flowGroup: "Reports" },
    { key: "emp_rpt_transfers", flowGroup: "Reports" },
    { key: "emp_rpt_resignations", flowGroup: "Reports" },
    { key: "employee_settings", flowGroup: "Settings" },
    { key: "emp_set_employee_code", flowGroup: "Settings" },
    { key: "emp_set_policies", flowGroup: "Settings" },
    { key: "emp_set_policy_onboarding", flowGroup: "Settings" },
    { key: "emp_set_policy_confirmation", flowGroup: "Settings" },
    { key: "emp_set_policy_transfer", flowGroup: "Settings" },
    { key: "emp_set_policy_separation", flowGroup: "Settings" },
    { key: "letters_management", flowGroup: "Settings" },
    { key: "letter_offer", flowGroup: "Settings" },
    { key: "letter_appointment", flowGroup: "Settings" },
    { key: "letter_transfer", flowGroup: "Settings" },
    { key: "letter_confirmation", flowGroup: "Settings" },
    { key: "letter_experience", flowGroup: "Settings" },
    { key: "letter_resignation", flowGroup: "Settings" },
    { key: "letter_promotion", flowGroup: "Settings" },
    { key: "letter_salary_increment", flowGroup: "Settings" },
    { key: "letter_probation_extension", flowGroup: "Settings" },
    { key: "letter_termination", flowGroup: "Settings" },
    { key: "letter_contract", flowGroup: "Settings" },
    { key: "letter_contract_renewal", flowGroup: "Settings" },
    { key: "letter_other", flowGroup: "Settings" },
  ],
  attendance: [
    { key: "attendance_dashboard", flowGroup: "Dashboard" },
    { key: "attendance_import", flowGroup: "Import & Export" },
    { key: "comp_off_opening_import", flowGroup: "Import & Export" },
    { key: "attendance_lop_reversal_import", flowGroup: "Import & Export" },
    { key: "attendance_import_checks", flowGroup: "Import & Export" },
    { key: "mark_attendance", flowGroup: "Bulk Actions" },
    { key: "attendance_checklist", flowGroup: "Checklist" },
    { key: "process_attendance", flowGroup: "Process" },
    { key: "att_proc_reconciliation", flowGroup: "Process" },
    { key: "att_proc_process", flowGroup: "Process" },
    { key: "att_rpt_register", flowGroup: "Process" },
    { key: "att_proc_checklist", flowGroup: "Process" },
    { key: "att_proc_preview_validate", flowGroup: "Process" },
    { key: "att_proc_finalise", flowGroup: "Process" },
    { key: "attendance_run_engine", flowGroup: "Legacy" },
    { key: "att_act_change_status", flowGroup: "Actions" },
    { key: "att_act_apply_leave", flowGroup: "Actions" },
    { key: "att_act_change_shift", flowGroup: "Actions" },
    { key: "att_act_change_wo", flowGroup: "Actions" },
    { key: "att_act_regularise_checks", flowGroup: "Actions" },
    { key: "att_act_short_leave", flowGroup: "Actions" },
    { key: "att_act_lop_reversal", flowGroup: "Actions" },
    { key: "att_act_leave_adjustment", flowGroup: "Actions" },
    { key: "att_act_leave_balance", flowGroup: "Actions" },
    { key: "att_act_comp_off_ledger", flowGroup: "Actions" },
    { key: "view_edit_checks", flowGroup: "Actions" },
    { key: "att_act_on_duty", flowGroup: "Actions" },
    { key: "att_act_overtime", flowGroup: "Actions" },
    { key: "att_act_restricted_holiday", flowGroup: "Actions" },
    { key: "att_action_hub", flowGroup: "Action Hub" },
    { key: "att_rpt_checks", flowGroup: "Reports" },
    { key: "att_rpt_daily", flowGroup: "Reports" },
    { key: "att_rpt_who_are_in", flowGroup: "Reports" },
    { key: "att_rpt_monthly", flowGroup: "Reports" },
    { key: "att_rpt_leave_balance", flowGroup: "Reports" },
    { key: "att_rpt_overtime", flowGroup: "Reports" },
    { key: "att_rpt_lop_reversal", flowGroup: "Reports" },
    { key: "comp_off_ledger_report", flowGroup: "Reports" },
    { key: "attendance_history", flowGroup: "Legacy" },
    { key: "attendance_general_settings", flowGroup: "Settings" },
    { key: "attendance_policies", flowGroup: "Settings" },
    { key: "attendance_weekly_off_settings", flowGroup: "Settings" },
    { key: "attendance_holiday_settings", flowGroup: "Settings" },
    { key: "attendance_leave_settings", flowGroup: "Settings" },
    { key: "attendance_shift_settings", flowGroup: "Settings" },
    { key: "attendance_policy_settings", flowGroup: "Settings" },
    { key: "attendance_codes_devices", flowGroup: "Settings" },
    { key: "attendance_code_mapping", flowGroup: "Settings" },
    { key: "attendance_devices", flowGroup: "Settings" },
  ],
  payroll: [
    { key: "payroll_dashboard", flowGroup: "Dashboard" },
    { key: "apply_auto_structures", flowGroup: "Dashboard" },
    { key: "structure_value_override", flowGroup: "Settings" },
    { key: "run_payroll", flowGroup: "Process" },
    { key: "pay_proc_new_joinees", flowGroup: "Process" },
    { key: "pay_proc_left_employees", flowGroup: "Process" },
    { key: "pay_proc_salary_revisions", flowGroup: "Process" },
    { key: "pay_proc_attendance_validation", flowGroup: "Process" },
    { key: "pay_proc_lop_reversal_arrears", flowGroup: "Process" },
    { key: "pay_proc_one_time_inputs", flowGroup: "Process" },
    { key: "pay_proc_bank_details", flowGroup: "Process" },
    { key: "pay_proc_headcount_reconcile", flowGroup: "Process" },
    { key: "pay_proc_process", flowGroup: "Process" },
    { key: "pay_rpt_projection", flowGroup: "Process" },
    { key: "pay_rpt_register", flowGroup: "Process" },
    { key: "pay_rpt_reconciliation", flowGroup: "Process" },
    { key: "pay_rpt_journal", flowGroup: "Process" },
    { key: "pay_rpt_unpaid", flowGroup: "Process" },
    { key: "pay_rpt_leave_encashment_away", flowGroup: "Process" },
    { key: "pay_proc_checklist", flowGroup: "Process" },
    { key: "pay_proc_disbursement", flowGroup: "Process" },
    { key: "pay_proc_preview_validate", flowGroup: "Process" },
    { key: "pay_proc_finalise", flowGroup: "Process" },
    { key: "pay_proc_payslips", flowGroup: "Process" },
    { key: "process_payroll", flowGroup: "Legacy" },
    { key: "assigned_structure", flowGroup: "Legacy" },
    { key: "loan", flowGroup: "Legacy" },
    { key: "leave_encashment", flowGroup: "Legacy" },
    { key: "payslips", flowGroup: "Legacy" },
    { key: "pay_act_assigned_structure", flowGroup: "Actions" },
    { key: "pay_act_salary_revision", flowGroup: "Actions" },
    { key: "pay_act_update_statutory", flowGroup: "Actions" },
    { key: "pay_act_tax_planning", flowGroup: "Actions" },
    { key: "pay_act_loan", flowGroup: "Actions" },
    { key: "pay_act_leave_encashment", flowGroup: "Actions" },
    { key: "pay_act_advance", flowGroup: "Actions" },
    { key: "pay_act_reimbursement", flowGroup: "Actions" },
    { key: "pay_act_reimb_optins", flowGroup: "Actions" },
    { key: "pay_act_reimb_claims", flowGroup: "Actions" },
    { key: "pay_act_reimb_ledger", flowGroup: "Actions" },
    { key: "pay_act_reimb_year_end", flowGroup: "Actions" },
    { key: "pay_act_process_fnf", flowGroup: "Actions" },
    { key: "pay_action_hub", flowGroup: "Action Hub" },
    { key: "payroll_checklist", flowGroup: "Checklist" },
    { key: "salary_revision_import", flowGroup: "Import & Export" },
    { key: "salary_structure_component_import", flowGroup: "Import & Export" },
    { key: "tax_declaration", flowGroup: "Import & Export" },
    { key: "salary_revision_report", flowGroup: "Reports" },
    { key: "unpaid_salary_report", flowGroup: "Reports" },
    { key: "statutory_filing", flowGroup: "Statutory Filing" },
    { key: "statutory_filing_pf", flowGroup: "Statutory Filing" },
    { key: "statutory_filing_esi", flowGroup: "Statutory Filing" },
    { key: "statutory_filing_pt", flowGroup: "Statutory Filing" },
    { key: "statutory_filing_nps", flowGroup: "Statutory Filing" },
    { key: "statutory_filing_meal", flowGroup: "Statutory Filing" },
    { key: "form24q_filing", flowGroup: "Statutory Filing" },
    { key: "tds_challans", flowGroup: "Statutory Filing" },
    { key: "payroll_settings", flowGroup: "Settings" },
    { key: "salary_structure", flowGroup: "Settings" },
    { key: "statutory_rates", flowGroup: "Settings" },
    { key: "tds_reference_heads", flowGroup: "Settings" },
    { key: "flexi_benefits", flowGroup: "Settings" },
    { key: "formula_manager", flowGroup: "Settings" },
    { key: "update_statutory_applicability", flowGroup: "Settings" },
    { key: "tds_engine_compliance", flowGroup: "Settings" },
    { key: "pay_set_policies", flowGroup: "Settings" },
    { key: "loan_policies", flowGroup: "Settings" },
    { key: "leave_encashment_policies", flowGroup: "Settings" },
  ],
  hiring: [
    { key: "dashboard", flowGroup: "Workspace" },
    { key: "mrf_jobs", flowGroup: "Requisition" },
    { key: "candidates", flowGroup: "Pipeline" },
    { key: "ats", flowGroup: "Pipeline" },
    { key: "referrals", flowGroup: "Pipeline" },
    { key: "hiring_settings", flowGroup: "Settings" },
  ],
  assets: [
    { key: "asset_dashboard", flowGroup: "Workspace" },
    { key: "asset_list", flowGroup: "Workspace" },
    { key: "assign_asset", flowGroup: "Workspace" },
    { key: "asset_requests", flowGroup: "Workspace" },
    { key: "asset_operations", flowGroup: "Workspace" },
    { key: "asset_settings", flowGroup: "Settings" },
    { key: "asset_import", flowGroup: "Import / Export" },
    { key: "asset_reports", flowGroup: "Reports" },
  ],
  communication: [
    { key: "flash_news", flowGroup: "Content" },
    { key: "announcements", flowGroup: "Content" },
    { key: "polls", flowGroup: "Polls" },
    { key: "company_policy", flowGroup: "Policies" },
  ],
  helpdesk: [
    { key: "tickets", flowGroup: "Workspace" },
    { key: "ticket_actions", flowGroup: "Workspace" },
    { key: "ticket_feedback", flowGroup: "Workspace" },
    { key: "helpdesk_config", flowGroup: "Catalog" },
    { key: "helpdesk_level_policies", flowGroup: "Catalog" },
  ],
  user_roles: [
    { key: "role_list", flowGroup: "Roles" },
    { key: "screen_access", flowGroup: "Access" },
    { key: "data_access", flowGroup: "Access" },
  ],
  ess_quick_panel: [
    { key: "leave_request", flowGroup: "My requests" },
    { key: "change_shift", flowGroup: "My requests" },
    { key: "change_wo", flowGroup: "My requests" },
    { key: "regularization_checks", flowGroup: "My requests" },
    { key: "short_leave", flowGroup: "My requests" },
    { key: "od", flowGroup: "My requests" },
    { key: "ot", flowGroup: "My requests" },
    { key: "restricted_holiday", flowGroup: "My requests" },
    { key: "ticket_request", flowGroup: "My requests" },
    { key: "tax_declaration", flowGroup: "My requests" },
    { key: "reimbursement_opt_in", flowGroup: "My requests" },
    { key: "reimbursement_claim", flowGroup: "My requests" },
    { key: "mrf_request", flowGroup: "My requests" },
    { key: "announcements", flowGroup: "Shortcuts" },
    { key: "comp_off", flowGroup: "Approvals" },
    { key: "onboarding_approval", flowGroup: "Approvals" },
    { key: "confirmation_approval", flowGroup: "Approvals" },
    { key: "transfer_approval", flowGroup: "Approvals" },
    { key: "exit_separation_approval", flowGroup: "Approvals" },
  ],
};

/**
 * Reorder screens by SCREEN_ACCESS_FLOW; merge flowGroup.
 * Unknown keys (new screens) append after known ones so they still appear.
 */
function applyModuleScreenAccessFlow(moduleDef, screens) {
  const flow = SCREEN_ACCESS_FLOW[moduleDef.key];
  if (!Array.isArray(flow) || flow.length === 0) return screens;

  const byKey = new Map(screens.map((s) => [s.key, s]));
  const ordered = [];
  const used = new Set();

  for (const item of flow) {
    const screen = byKey.get(item.key);
    if (!screen) continue;
    ordered.push({
      ...screen,
      flowGroup: screen.flowGroup || item.flowGroup || undefined,
      parentKey: screen.parentKey || undefined,
    });
    used.add(item.key);
  }

  for (const screen of screens) {
    if (used.has(screen.key)) continue;
    ordered.push(screen);
  }

  return ordered;
}

const attachStrictMeta = (catalog) =>
  catalog.map((moduleDef) => {
    const screensRaw = (moduleDef.screens || []).map((screenDef) => ({
      ...screenDef,
      screenName: screenDef.label || toTitleCase(screenDef.key),
      capabilities: deriveCapabilities(screenDef),
      actionLabels: buildActionLabels(screenDef),
      actionWhere: normalizeActionWhere(screenDef.actionWhere, screenDef.actions),
    }));
    const screens = applyModuleScreenAccessFlow(moduleDef, screensRaw);
    const moduleCapabilities = OPERATION_CAPABILITIES.filter((op) =>
      screens.some((screenDef) => screenDef.capabilities.includes(op))
    );
    return {
      ...moduleDef,
      moduleName: moduleDef.label || toTitleCase(moduleDef.key),
      capabilities: moduleCapabilities,
      screens,
    };
  });

// The actions listed per screen. Every screen supports `view`; the rest are
// declared only when they make sense (e.g. Reports has `view` + `export`).

/** Employee self-service profile tabs (ESS + optional admin preview). Not in admin top-nav catalog. */
const PROFILE_MODULE_RAW = {
  key: "profile",
  label: "Profile",
  path: "/employee/profile",
  description:
    "Employee self-service profile tabs shown in the left employee panel profile page.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "org_details",
      label: "Org Details",
      description: "Organization details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Org Details",
        edit: "Edit Org Details",
      },
    },
    {
      key: "personal_details",
      label: "Personal",
      description: "Personal details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Personal",
        edit: "Edit Personal",
      },
    },
    {
      key: "family_details",
      label: "Family",
      description: "Family details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Family",
        edit: "Edit Family",
      },
    },
    {
      key: "bank_details",
      label: "Bank",
      description: "Bank details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Bank",
        edit: "Edit Bank / Update & delete bank history",
      },
      actionWhere: {
        view: "(Employee profile — Bank tab)",
        edit: "(Employee profile — Bank tab; Update bank details, eye for Bank Proof, delete entry)",
      },
    },
    {
      key: "education_details",
      label: "Education",
      description: "Education details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Education",
        edit: "Edit Education",
      },
    },
    {
      key: "profession_details",
      label: "Profession",
      description: "Profession details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Profession",
        edit: "Edit Profession",
      },
    },
    {
      key: "statutory_details",
      label: "Statutory Details",
      description: "Statutory details tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Statutory Details",
        edit: "Edit Statutory Details",
      },
    },
    {
      key: "profile_documents",
      label: "Documents",
      description: "Documents tab inside employee profile.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Documents",
        edit: "Edit Documents",
      },
    },
  ],
};

/**
 * Historical / alternate `ess_quick_panel.screens` keys stored in DB or returned
 * by older `me/permissions` merges. Values are merged (OR per action) into the
 * canonical catalog key before `normalizeAccess` runs.
 *
 * - `regularization` → UI catalog uses `regularization_checks`.
 * - `review_requests` → closest ESS shortcut is pending approvals queue.
 */
export const ESS_QUICK_PANEL_LEGACY_SCREEN_ALIASES = {
  regularization: "regularization_checks",
  review_requests: "leave_request",
};

/** Old single key → Opt-in + Claim (both request types under My requests). */
export const ESS_REIMBURSEMENT_LEGACY_KEY = "reimbursement";
export const ESS_REIMBURSEMENT_SCREEN_KEYS = [
  "reimbursement_opt_in",
  "reimbursement_claim",
];

/**
 * Merges legacy quick-panel screen keys into canonical catalog keys. Leaves
 * other keys untouched (e.g. `announcements` when present in catalog).
 */
export function expandEssQuickPanelScreens(screens = {}) {
  if (!screens || typeof screens !== "object") return screens;
  const out = { ...screens };
  for (const [legacyKey, canonicalKey] of Object.entries(
    ESS_QUICK_PANEL_LEGACY_SCREEN_ALIASES
  )) {
    const leg = out[legacyKey];
    if (!leg || typeof leg !== "object") continue;
    const existing =
      out[canonicalKey] && typeof out[canonicalKey] === "object"
        ? out[canonicalKey]
        : {};
    const merged = { ...existing };
    for (const action of ACTIONS) {
      if (leg[action]) merged[action] = true;
    }
    out[canonicalKey] = merged;
  }

  // reimbursement → reimbursement_opt_in + reimbursement_claim
  const legacyReimb = out[ESS_REIMBURSEMENT_LEGACY_KEY];
  if (legacyReimb && typeof legacyReimb === "object") {
    for (const canonicalKey of ESS_REIMBURSEMENT_SCREEN_KEYS) {
      const existing =
        out[canonicalKey] && typeof out[canonicalKey] === "object"
          ? out[canonicalKey]
          : {};
      const merged = { ...existing };
      for (const action of ACTIONS) {
        if (legacyReimb[action]) merged[action] = true;
      }
      out[canonicalKey] = merged;
    }
  }
  return out;
}

/**
 * Attendance → Actions hub screens (Process Attendance → Actions).
 * Keys are new; when none are stored yet, inherit from the older coarse gates
 * (mark_attendance / process_attendance / leave_requests / regularization) so
 * existing roles keep the same Actions visibility until an admin re-saves.
 * `view_edit_checks` already existed and is not part of this migration marker.
 */
export const ATTENDANCE_ACTION_SCREEN_KEYS = [
  "att_act_change_status",
  "att_act_apply_leave",
  "att_act_change_shift",
  "att_act_change_wo",
  "att_act_regularise_checks",
  "att_act_short_leave",
  "att_act_lop_reversal",
  "att_act_leave_adjustment",
  "att_act_leave_balance",
  "att_act_comp_off_ledger",
  "att_act_on_duty",
  "att_act_overtime",
  "att_act_restricted_holiday",
];

const emptyActionRow = () => {
  const row = {};
  for (const a of ACTIONS) row[a] = false;
  return row;
};

const actionRow = (flags = {}) => {
  const row = emptyActionRow();
  for (const [k, v] of Object.entries(flags)) {
    if (ACTIONS.includes(k) && v) row[k] = true;
  }
  return row;
};

/**
 * Seeds missing Attendance Action screens from legacy coarse permissions.
 * If any `ATTENDANCE_ACTION_SCREEN_KEYS` entry already exists on the role,
 * the role is treated as migrated and left unchanged.
 * @returns {object} raw screenAccess (new object when expanded; same ref when noop)
 */
export function expandAttendanceActionScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const att = raw.attendance;
  if (!att || typeof att !== "object") return raw;
  const screens =
    att.screens && typeof att.screens === "object" ? { ...att.screens } : {};
  if (ATTENDANCE_ACTION_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const mark = screens.mark_attendance || {};
  const process = screens.process_attendance || {};
  const leave = screens.leave_requests || {};
  const reg = screens.regularization || {};
  const ledger = screens.comp_off_ledger_report || {};

  // Mirror prior AttendanceActionsHub coarse gates.
  const canViewAtt = !!(att.view || mark.view || process.view);
  const canModify = !!(mark.create || mark.edit);
  const canLeaveOps = !!(mark.create || leave.create || leave.approve);
  const canApprove = !!(leave.approve || reg.approve || process.approve || mark.edit);
  const canCompOff = !!(canModify || ledger.view || canViewAtt);

  screens.att_act_change_status = actionRow({ view: canViewAtt, edit: canModify });
  screens.att_act_apply_leave = actionRow({ view: canLeaveOps, create: canLeaveOps });
  screens.att_act_change_shift = actionRow({ view: canModify, edit: canModify });
  screens.att_act_change_wo = actionRow({ view: canModify, edit: canModify });
  screens.att_act_regularise_checks = actionRow({
    view: canModify || !!reg.create || !!reg.view,
    create: canModify || !!reg.create,
  });
  screens.att_act_short_leave = actionRow({ view: canModify, create: canModify });
  screens.att_act_lop_reversal = actionRow({ view: canApprove, edit: canApprove });
  screens.att_act_leave_adjustment = actionRow({ view: canModify, edit: canModify });
  screens.att_act_leave_balance = actionRow({ view: canModify || !!leave.view });
  screens.att_act_comp_off_ledger = actionRow({ view: canCompOff });
  screens.att_act_on_duty = actionRow({ view: canModify, create: canModify });
  screens.att_act_overtime = actionRow({ view: canModify, create: canModify });
  screens.att_act_restricted_holiday = actionRow({ view: canModify, create: canModify });

  return {
    ...raw,
    attendance: { ...att, screens },
  };
}

/** Payroll → Actions hub screens (Run Payroll → Actions). */
export const PAYROLL_ACTION_SCREEN_KEYS = [
  "pay_act_assigned_structure",
  "pay_act_salary_revision",
  "pay_act_update_statutory",
  "pay_act_tax_planning",
  "pay_act_loan",
  "pay_act_leave_encashment",
  "pay_act_advance",
  "pay_act_reimbursement",
  "pay_act_process_fnf",
];

/**
 * Seeds missing Payroll Action screens from legacy coarse grants
 * (assigned_structure / loan / leave_encashment / run_payroll / update_statutory).
 */
export function expandPayrollActionScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const pay = raw.payroll;
  if (!pay || typeof pay !== "object") return raw;
  const screens =
    pay.screens && typeof pay.screens === "object" ? { ...pay.screens } : {};
  if (PAYROLL_ACTION_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const assigned = screens.assigned_structure || {};
  const run = screens.run_payroll || {};
  const loan = screens.loan || {};
  const encash = screens.leave_encashment || {};
  const statutory = screens.update_statutory_applicability || {};
  const revImport = screens.salary_revision_import || {};

  const workspace = !!(pay.view || assigned.view || run.view);
  const canRevise = !!(assigned.edit || revImport.create || revImport.import || run.edit);
  const canStatutory = !!(statutory.edit || assigned.edit || run.edit);
  const canLoan = !!(loan.view || loan.create || run.view || workspace);
  const canEncash = !!(encash.view || encash.create || run.view || workspace);

  screens.pay_act_assigned_structure = actionRow({
    view: workspace || !!assigned.view,
    edit: !!(assigned.edit || run.edit),
  });
  screens.pay_act_salary_revision = actionRow({
    view: canRevise || workspace,
    edit: canRevise,
  });
  screens.pay_act_update_statutory = actionRow({
    view: canStatutory || workspace,
    edit: canStatutory,
  });
  screens.pay_act_tax_planning = actionRow({
    view: workspace,
    edit: !!(run.edit || assigned.edit || workspace),
  });
  screens.pay_act_loan = actionRow({
    view: canLoan,
    create: !!(loan.create || run.create || canLoan),
    edit: !!(loan.edit || run.edit),
    approve: !!(loan.approve || run.approve),
  });
  screens.pay_act_leave_encashment = actionRow({
    view: canEncash,
    create: !!(encash.create || run.create || canEncash),
    edit: !!(encash.edit || run.edit),
    approve: !!(encash.approve || run.approve),
  });
  screens.pay_act_advance = actionRow({
    view: workspace,
    create: !!(run.create || run.edit || workspace),
    edit: !!(run.edit || workspace),
  });
  screens.pay_act_reimbursement = actionRow({
    view: workspace,
    create: !!(run.create || run.edit || workspace),
    edit: !!(run.edit || workspace),
  });
  screens.pay_act_process_fnf = actionRow({
    view: workspace,
    edit: !!(run.edit || run.approve || workspace),
    approve: !!(run.approve || workspace),
  });

  return { ...raw, payroll: { ...pay, screens } };
}

/** Reimbursement Control Center tabs (Actions → Reimbursement). */
export const PAYROLL_REIMB_CONTROL_SCREEN_KEYS = [
  "pay_act_reimb_optins",
  "pay_act_reimb_claims",
  "pay_act_reimb_ledger",
  "pay_act_reimb_year_end",
];

/**
 * Seeds Control Center screens from flexi_benefits / pay_act_reimbursement when
 * none of the new keys are stored yet (existing roles keep access until re-saved).
 */
export function expandPayrollReimbControlScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const pay = raw.payroll;
  if (!pay || typeof pay !== "object") return raw;
  const screens =
    pay.screens && typeof pay.screens === "object" ? { ...pay.screens } : {};
  if (PAYROLL_REIMB_CONTROL_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const flexi = screens.flexi_benefits || {};
  const act = screens.pay_act_reimbursement || {};
  const canView = !!(
    flexi.view ||
    flexi.approve ||
    act.view ||
    act.edit ||
    act.create
  );
  if (!canView) return raw;

  screens.pay_act_reimb_optins = actionRow({
    view: canView,
    create: !!(flexi.create || flexi.edit || act.create || act.edit),
    edit: !!(flexi.edit || act.edit || flexi.approve),
  });
  screens.pay_act_reimb_claims = actionRow({
    view: canView,
    approve: !!(flexi.approve || act.edit),
  });
  screens.pay_act_reimb_ledger = actionRow({
    view: canView,
    export: !!(flexi.view || act.view || canView),
  });
  screens.pay_act_reimb_year_end = actionRow({
    view: canView,
    edit: !!(flexi.edit || flexi.approve || act.edit),
  });

  return { ...raw, payroll: { ...pay, screens } };
}

/** Employee → Actions hub screens (Employees → Actions). */
export const EMPLOYEE_ACTION_SCREEN_KEYS = [
  "emp_act_view_employee",
  "emp_act_generate_letter",
  "emp_act_confirmation",
  "emp_act_transfer",
  "emp_act_resignation",
];

/**
 * Seeds missing Employee Action screens from edit_employee / letters / lifecycle screens.
 */
export function expandEmployeeActionScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const emp = raw.employee;
  if (!emp || typeof emp !== "object") return raw;
  const screens =
    emp.screens && typeof emp.screens === "object" ? { ...emp.screens } : {};
  if (EMPLOYEE_ACTION_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const edit = screens.edit_employee || {};
  const list = screens.employee_list || {};
  const letters = screens.letters_management || {};
  const settings = screens.employee_settings || {};
  const conf = screens.confirmation || {};
  const xfer = screens.transfer || {};
  const resign = screens.resignation || {};

  const canViewEmp = !!(edit.view || edit.edit || list.view || list.edit);
  const canLetters = !!(
    letters.view ||
    letters.create ||
    settings.view ||
    settings.edit
  );
  const canConf = !!(conf.create || conf.approve || conf.view);
  const canXfer = !!(xfer.create || xfer.edit || xfer.view);
  const canResign = !!(resign.create || resign.approve || resign.view);

  screens.emp_act_view_employee = actionRow({
    view: canViewEmp,
    edit: !!(edit.edit || edit.view || list.edit),
  });
  screens.emp_act_generate_letter = actionRow({
    view: canLetters,
    create: canLetters,
  });
  screens.emp_act_confirmation = actionRow({
    view: canConf,
    create: !!(conf.create || conf.approve),
    approve: !!conf.approve,
  });
  screens.emp_act_transfer = actionRow({
    view: canXfer,
    create: !!(xfer.create || xfer.edit),
    edit: !!xfer.edit,
  });
  screens.emp_act_resignation = actionRow({
    view: canResign,
    create: !!(resign.create || resign.approve),
    approve: !!resign.approve,
  });

  return { ...raw, employee: { ...emp, screens } };
}

/** Admin Employee Profile tab screens (nested under employee_profile). */
export const EMPLOYEE_PROFILE_TAB_SCREEN_KEYS = [
  "org_details",
  "personal_details",
  "family_details",
  "bank_details",
  "education_details",
  "profession_details",
  "statutory_details",
  "profile_documents",
];

/**
 * Seeds missing Employee Profile tab screens from edit_employee / employee_profile /
 * employee_documents legacy grants.
 */
export function expandEmployeeProfileScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const emp = raw.employee;
  if (!emp || typeof emp !== "object") return raw;
  const screens =
    emp.screens && typeof emp.screens === "object" ? { ...emp.screens } : {};
  if (EMPLOYEE_PROFILE_TAB_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const edit = screens.edit_employee || {};
  const profile = screens.employee_profile || {};
  const docs = screens.employee_documents || {};
  const canView = !!(
    edit.view ||
    edit.edit ||
    profile.view ||
    profile.edit ||
    docs.view ||
    docs.edit ||
    docs.create
  );
  const canEdit = !!(edit.edit || profile.edit || docs.edit || docs.create);
  if (!canView && !canEdit) return raw;

  const tabRow = actionRow({ view: canView || canEdit, edit: canEdit });
  for (const key of EMPLOYEE_PROFILE_TAB_SCREEN_KEYS) {
    if (key === "profile_documents") {
      screens.profile_documents = actionRow({
        view: canView || canEdit || !!(docs.view || docs.export),
        create: !!(docs.create || canEdit),
        edit: canEdit || !!docs.edit,
        export: !!(docs.export || docs.view || canEdit),
      });
    } else {
      screens[key] = { ...tabRow };
    }
  }
  if (screens.employee_profile == null) {
    screens.employee_profile = actionRow({ view: true, edit: canEdit });
  }

  return { ...raw, employee: { ...emp, screens } };
}

/**
 * Attendance Settings sub-tabs (new keys). Existing
 * attendance_general_settings / attendance_policy_settings are also seeded when missing.
 */
export const ATTENDANCE_SETTINGS_SCREEN_KEYS = [
  "attendance_weekly_off_settings",
  "attendance_holiday_settings",
  "attendance_leave_settings",
  "attendance_shift_settings",
  "attendance_code_mapping",
  "attendance_devices",
];

/**
 * Seeds Attendance Settings screens from legacy coarse gates
 * (mark_attendance / module edit / existing general+policy / configuration.attendance_codes).
 */
export function expandAttendanceSettingsScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const att = raw.attendance;
  if (!att || typeof att !== "object") return raw;
  const screens =
    att.screens && typeof att.screens === "object" ? { ...att.screens } : {};
  if (ATTENDANCE_SETTINGS_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }

  const mark = screens.mark_attendance || {};
  const gen = screens.attendance_general_settings || {};
  const pol = screens.attendance_policy_settings || {};
  const cfgCodes =
    raw.configuration?.screens?.attendance_codes &&
    typeof raw.configuration.screens.attendance_codes === "object"
      ? raw.configuration.screens.attendance_codes
      : {};

  const legacyManage = !!(
    att.edit ||
    att.create ||
    mark.edit ||
    mark.create ||
    gen.view ||
    gen.edit ||
    pol.view ||
    pol.create ||
    pol.edit
  );
  const policyAccess = !!(pol.view || pol.create || pol.edit || legacyManage);
  const generalAccess = !!(gen.view || gen.edit || legacyManage);
  // Codes & devices are NOT inherited from General / Policy settings grants; only from
  // Mark Attendance / module-level edit-create or the legacy Configuration attendance_codes screen.
  const markManage = !!(att.edit || att.create || mark.edit || mark.create);
  const codesAccess = !!(cfgCodes.view || cfgCodes.edit || markManage);

  if (screens.attendance_general_settings == null) {
    screens.attendance_general_settings = actionRow({
      view: generalAccess,
      edit: !!(gen.edit || legacyManage),
    });
  }
  if (screens.attendance_policy_settings == null) {
    screens.attendance_policy_settings = actionRow({
      view: policyAccess,
      create: !!(pol.create || legacyManage),
      edit: !!(pol.edit || legacyManage),
    });
  }

  const policyRow = actionRow({
    view: policyAccess,
    create: !!(pol.create || legacyManage),
    edit: !!(pol.edit || legacyManage),
  });
  screens.attendance_weekly_off_settings = { ...policyRow };
  screens.attendance_holiday_settings = { ...policyRow };
  screens.attendance_leave_settings = { ...policyRow };
  screens.attendance_shift_settings = { ...policyRow };
  screens.attendance_code_mapping = actionRow({
    view: codesAccess,
    edit: !!(cfgCodes.edit || markManage),
  });
  screens.attendance_devices = actionRow({
    view: codesAccess,
    create: markManage,
    edit: markManage,
  });

  return { ...raw, attendance: { ...att, screens } };
}

/** ESS-only: right Quick Panel shortcuts (EmployeeSidebar.jsx rightItems). Granular request and approval types. */
const ESS_QUICK_PANEL_MODULE_RAW = {
  key: "ess_quick_panel",
  label: "Quick Panel",
  path: "/employee",
  description:
    "Request / approval shortcuts. Enabled screens also control My requests type list and ESS Attendance Logs ⋮ raise actions (attendance types still need policy).",
  actions: ["view"],
  screens: [
    {
      key: "leave_request",
      label: "Leave Request",
      description:
        "Submit, view, and request cancellation of approved leave. Pending cancel items show as Leave cancellation on My requests / Approvals.",
      actions: ["view"],
      actionLabels: { view: "Show Leave Requests" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Leave · ESS Attendance Logs ⋮ → Apply Leave — also requires attendance policy; Leave form footer → View history)",
      },
    },
    {
      key: "change_shift",
      label: "Change Shift",
      description:
        "Request shift change. Gates My requests and ESS Attendance Logs ⋮ when policy enables Change Shift.",
      actions: ["view"],
      actionLabels: { view: "Show Change Shift" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Change Shift · Attendance Logs ⋮ — Quick Panel ∧ policy; form footer → View history)",
      },
    },
    {
      key: "change_wo",
      label: "Change WO",
      description:
        "Request to work on a full-day Weekly Off. Gates My requests and Attendance Logs ⋮ when Weekly Off policy has Override Weekly Off enabled.",
      actions: ["view"],
      actionLabels: { view: "Show Change WO" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Change WO · Attendance Logs ⋮ — Quick Panel ∧ Weekly Off override; form footer → View history)",
      },
    },
    {
      key: "resignation",
      label: "Resignation",
      description: "Submit resignation request with policy notice, shortfall, and checklist.",
      actions: ["view"],
      actionLabels: { view: "Show Resignation" },
      actionWhere: {
        view:
          "(Request → Resignation — New Resignation form: policy, dates, shortfall, checklist; ⓘ info for hints; footer → View history)",
      },
    },
    {
      key: "regularization_checks",
      label: "Regularization Checks",
      description:
        "Attendance regularization. Gates My requests and ESS Attendance Logs ⋮ when policy allows regularization.",
      actions: ["view"],
      actionLabels: { view: "Show Regularization Checks" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Regularise checks · Attendance Logs ⋮ — Quick Panel ∧ policy; form footer → View history)",
      },
    },
    {
      key: "ticket_request",
      label: "Ticket",
      description: "Helpdesk ticket submission and tracking.",
      actions: ["view"],
      actionLabels: { view: "Show Tickets" },
      actionWhere: {
        view: "(Quick Panel · My requests → Ticket; form footer → View history)",
      },
    },
    {
      key: "tax_declaration",
      label: "Tax Planning",
      description: "Tax planning forms and submissions.",
      actions: ["view"],
      actionLabels: { view: "Show Tax Planning" },
      actionWhere: {
        view: "(Quick Panel · My requests → Tax Planning; form footer → View history)",
      },
    },
    {
      key: "reimbursement_opt_in",
      label: "Reimbursement Opt-in",
      description:
        "Declare / opt-in for flexi reimbursement components for the financial year (up to eligible ceiling).",
      actions: ["view"],
      actionLabels: { view: "Show Reimbursement Opt-in" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Reimbursement Opt-in) or (Employee → Payroll → Reimbursement · Opt-in)",
      },
    },
    {
      key: "reimbursement_claim",
      label: "Reimbursement Claim",
      description:
        "Submit reimbursement claims against opted-in balances (bills / claim period).",
      actions: ["view"],
      actionLabels: { view: "Show Reimbursement Claim" },
      actionWhere: {
        view:
          "(Quick Panel · My requests → Reimbursement Claim) or (Employee → Payroll → Reimbursement · Claim)",
      },
    },
    {
      key: "mrf_request",
      label: "MRF",
      description: "Material Request Form submissions.",
      actions: ["view"],
      actionLabels: { view: "Show MRF" },
      actionWhere: {
        view: "(Quick Panel · My requests → MRF; form footer → View history)",
      },
    },
    {
      key: "short_leave",
      label: "Short Leave",
      description:
        "Short leave requests. Gates My requests and Attendance Logs ⋮ when policy enables Short Leave.",
      actions: ["view"],
      actionLabels: { view: "Show Short Leave" },
      actionWhere: {
        view: "(Quick Panel · My requests / Attendance Logs ⋮ — Quick Panel ∧ policy; form footer → View history)",
      },
    },
    {
      key: "od",
      label: "On Duty",
      description:
        "On Duty (OD) requests. Visibility follows Quick Panel ∧ attendance policy On Duty enabled.",
      actions: ["view"],
      actionLabels: { view: "Show On Duty" },
      actionWhere: {
        view:
          "(Quick Panel · My requests / Attendance Logs ⋮ → On Duty — Quick Panel ∧ policy; Attendance → Process → Actions → On Duty; form footer → View history)",
      },
    },
    {
      key: "comp_off",
      label: "Comp Off Approvals",
      description:
        "View and act on Comp Off Credit approvals. Employees avail Comp Off via Leave type CO (My requests → Leave), not a separate Comp Off request type.",
      actions: ["view"],
      actionLabels: { view: "Show Comp Off Credit in Approvals" },
      actionWhere: {
        view:
          "(Quick Panel · Approvals → Comp Off Credit — RM queue after Regularise / WO-PH work)",
      },
    },
    {
      key: "ot",
      label: "Overtime",
      description:
        "Overtime requests. Visibility follows Quick Panel ∧ attendance policy Overtime enabled.",
      actions: ["view"],
      actionLabels: { view: "Show Overtime" },
      actionWhere: {
        view:
          "(Quick Panel · My requests / Attendance Logs ⋮ → Overtime — Quick Panel ∧ policy; Attendance → Process → Actions → Overtime; form footer → View history)",
      },
    },
    {
      key: "restricted_holiday",
      label: "Restricted Holiday",
      description:
        "Apply for a Restricted Holiday (RH) date from the holiday policy calendar. RM approves via Quick Panel Approvals.",
      actions: ["view"],
      actionLabels: { view: "Show Restricted Holiday" },
      actionWhere: {
        view:
          "(ESS right sidebar · Restricted Holiday; Attendance · Apply Restricted Holiday; calendar ⋮; Attendance → Process → Actions → Restricted Holiday; My Requests type filter; form footer → View history)",
      },
    },
    {
      key: "announcements",
      label: "Announcements",
      description:
        "Company announcements shortcut (parity with stored screen_access keys; wire in ESS UI when needed).",
      actions: ["view"],
      actionLabels: { view: "Show Announcements" },
    },
    {
      key: "onboarding_approval",
      label: "Employee Onboarding Approval",
      description: "Approve or reject employee onboarding requests in Quick Panel (after profile 100%).",
      actions: ["view", "approve"],
      actionLabels: {
        view: "View Onboarding Approvals",
        approve: "Approve / Reject Onboarding",
      },
      actionWhere: {
        view: "(Admin Quick Panel Approvals, or ESS right panel Approvals)",
        approve: "(approval desk - Approve / Reject; Approve disabled until profile 100%)",
      },
    },
    {
      key: "onboarding_task",
      label: "Onboarding Task",
      description:
        "Verify post-onboarding checklist tasks; mark Done or Reject with remarks.",
      actions: ["view", "approve"],
      actionLabels: {
        view: "View Onboarding Tasks",
        approve: "Done / Reject Checklist Task",
      },
      actionWhere: {
        view: "(Quick Panel - Approvals - Onboarding Task)",
        approve: "(task desk - Done / Reject buttons; Done disabled until profile 100%)",
      },
    },
    {
      key: "confirmation_approval",
      label: "Confirmation Approval",
      description:
        "Approve or reject employee confirmation / probation workflow steps and checklist tasks.",
      actions: ["view", "approve"],
      actionLabels: {
        view: "View Confirmation Approvals",
        approve: "Approve / Reject Confirmation",
      },
      actionWhere: {
        view: "(Admin or ESS Quick Panel → Approvals → Confirmation Approval)",
        approve: "(approval desk - Approve / Reject with remarks)",
      },
    },
    {
      key: "transfer_approval",
      label: "Transfer Approval",
      description: "Approve or reject employee transfer workflow steps and checklist tasks.",
      actions: ["view", "approve"],
      actionLabels: {
        view: "View Transfer Approvals",
        approve: "Approve / Reject Transfer",
      },
      actionWhere: {
        view: "(Admin or ESS Quick Panel → Approvals → Transfer Approval)",
        approve: "(approval desk - Approve / Reject with remarks)",
      },
    },
    {
      key: "exit_separation_approval",
      label: "Resignation / Termination Approval",
      description:
        "Approve or reject resignation / termination workflow steps, clearance, and checklist tasks.",
      actions: ["view", "approve"],
      actionLabels: {
        view: "View Resignation / Termination Approvals",
        approve: "Approve / Reject Separation",
      },
      actionWhere: {
        view:
          "(Admin or ESS Quick Panel → Approvals → Resignation Approval / Termination Approval)",
        approve: "(approval desk - Approve / Reject with remarks)",
      },
    },
  ],
};

const HOME_MODULE_RAW = {
  key: "home",
  label: "Home",
  path: "/home",
  description: "Home dashboard — employee landing page and admin celebration/glance widgets.",
  actions: ["view"],
  screens: [
    {
      key: "dashboard",
      label: "Dashboard",
      description: "Main home page content and employee landing screen.",
      actions: ["view"],
      actionLabels: { view: "View Home" },
      actionWhere: { view: "(home page)" },
    },
    {
      key: "birthdays",
      label: "Birthdays",
      description: "Upcoming employee birthdays widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show Birthdays Widget" },
      actionWhere: { view: "(home dashboard - Birthdays card)" },
    },
    {
      key: "work_anniversaries",
      label: "Work Anniversaries",
      description: "Upcoming work anniversaries widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show Work Anniversaries Widget" },
      actionWhere: { view: "(home dashboard - Work Anniversaries card)" },
    },
    {
      key: "new_joinees",
      label: "New Joinees",
      description: "Upcoming and recent new joinee widget on the home dashboard.",
      actions: ["view"],
      actionLabels: { view: "Show New Joinees Widget" },
      actionWhere: { view: "(home dashboard - New Joinees card)" },
    },
    {
      key: "glance",
      label: "Today at a Glance",
      description:
        "Admin: workforce headcount summary. ESS: personal punch, hours, leave total, and my pending requests.",
      actions: ["view"],
      actionLabels: { view: "Show Today at a Glance Widget" },
      actionWhere: { view: "(home dashboard - Today at a Glance section)" },
    },
    {
      key: "ess_attendance_calendar",
      label: "ESS Attendance Calendar",
      description: "Compact personal attendance month calendar on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show Attendance Calendar Widget" },
      actionWhere: { view: "(ESS home - Attendance calendar card)" },
    },
    {
      key: "ess_leave_balance",
      label: "ESS Leave Balance",
      description: "Leave balance tiles on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show Leave Balance Widget" },
      actionWhere: { view: "(ESS home - Leave balance card)" },
    },
    {
      key: "ess_upcoming_holidays",
      label: "ESS Upcoming Holidays",
      description: "Upcoming holidays strip on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show Upcoming Holidays Widget" },
      actionWhere: { view: "(ESS home - Upcoming holidays card)" },
    },
    {
      key: "ess_my_requests",
      label: "ESS My Requests",
      description: "Recent leave/request status list on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show My Requests Widget" },
      actionWhere: { view: "(ESS home - My requests card)" },
    },
    {
      key: "ess_team_away",
      label: "ESS Team Away",
      description: "Reportees away today widget on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show Team Away Widget" },
      actionWhere: { view: "(ESS home - Team away today card)" },
    },
    {
      key: "ess_payslip_tax",
      label: "ESS Payslip & Tax",
      description: "Payslip and tax planning shortcuts on ESS Home.",
      actions: ["view"],
      actionLabels: { view: "Show Payslip & Tax Widget" },
      actionWhere: { view: "(ESS home - Payslip & tax card)" },
    },
  ],
};

const ESS_ATTENDANCE_MODULE_RAW = {
  key: "ess_attendance",
  label: "ESS Attendance",
  path: "/employee/attendance",
  description: "Employee self-service attendance.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "self_service",
      label: "Self Service",
      description: "Employee attendance calendar, check-in/out, and requests.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Attendance",
        edit: "Check-in/out, regularise, or submit attendance actions",
      },
      actionWhere: {
        view: "(ESS Attendance calendar)",
        edit: "(navbar Check In/Out — saved to attendance by policy; calendar day actions)",
      },
    },
    {
      key: "leave_balance",
      label: "Leave Balance",
      description:
        "View own leave balance (opening/credited/availed/closing) from ESS Attendance day actions.",
      actions: ["view"],
      actionLabels: { view: "Open Leave Balance" },
      actionWhere: {
        view: "(ESS Attendance → day actions ⋮ → Leave Balance)",
      },
    },
    {
      key: "comp_off_ledger",
      label: "Comp Off Ledger",
      description:
        "View own Comp Off earn and avail lots (worked date, expiry, remaining days) from ESS Attendance.",
      actions: ["view"],
      actionLabels: { view: "Open Comp Off Ledger" },
      actionWhere: {
        view: "(ESS Attendance → day actions ⋮ → Comp Off Ledger)",
      },
    },
    {
      key: "view_checks",
      label: "View Checks",
      description:
        "Open the View checks form from the attendance calendar (read-only punches, location or device).",
      actions: ["view"],
      actionLabels: { view: "Open View checks" },
      actionWhere: {
        view: "(ESS Attendance → day actions ⋮ → View checks)",
      },
    },
  ],
};

const ESS_PAYROLL_MODULE_RAW = {
  key: "ess_payroll",
  label: "ESS Payroll",
  path: "/employee/payroll",
  description: "Employee self-service payroll summary, payslips, and Form-16.",
  actions: ["view", "edit"],
  screens: [
    {
      key: "self_service",
      label: "Self Service",
      description: "My Payroll Summary, Payslips, and Form-16 tabs.",
      actions: ["view", "edit"],
      actionLabels: {
        view: "View Payroll Summary / Payslips (PDF preview & download) / Form-16",
        edit: "Open Tax Planning from Requests (if applicable)",
      },
      actionWhere: {
        view:
          "(Employee → Payroll — My Payroll Summary | Payslips PDF preview/download | Form-16)",
        edit: "(Requests → Tax Planning)",
      },
    },
  ],
};

const ESS_REPORTS_MODULE_RAW = {
  key: "ess_reports",
  label: "ESS Reports",
  path: "/employee/reports",
  description:
    "Legacy route — redirects to Employee → Payroll (Payslips / Form-16).",
  actions: ["view", "export"],
  screens: [
    {
      key: "reports",
      label: "Reports",
      description: "Redirects to Payroll tabs for payslips and Form-16.",
      actions: ["view", "export"],
      actionLabels: {
        view: "Redirect to Payroll → Payslips",
        export: "Export (via Payroll documents when available)",
      },
      actionWhere: {
        view: "(legacy /employee/reports → /employee/payroll?tab=payslips)",
        export: "(Payroll → Payslips / Form-16)",
      },
    },
  ],
};

const ESS_ASSETS_MODULE_RAW = {
  key: "ess_assets",
  label: "ESS My Assets",
  path: "/employee/my-assets",
  description: "Employee self-service assigned assets and asset requests.",
  actions: ["view", "create"],
  screens: [
    {
      key: "self_service",
      label: "My Assets",
      description: "View assigned assets and submit asset requests.",
      actions: ["view", "create"],
      actionLabels: {
        view: "View My Assets",
        create: "Request Asset",
      },
      actionWhere: {
        view: "(ESS → My Assets)",
        create: "(My Assets → Request asset)",
      },
    },
  ],
};

const MY_TEAM_MODULE_RAW = {
  key: "my_team",
  label: "My Team",
  path: "/employee/my-team",
  description: "Direct reportees list and manager self-service.",
  actions: ["view"],
  screens: [
    {
      key: "reportee_list",
      label: "Team list",
      description: "View and interact with direct reportees.",
      actions: ["view"],
      actionLabels: { view: "View My Team" },
      actionWhere: { view: "(ESS → My Team — team member cards)" },
    },
    {
      key: "change_shift_for_reportee",
      label: "Change Shift for team",
      description:
        "Apply Change Shift for a direct reportee from My Team (RM self-approval).",
      actions: ["view", "create"],
      actionLabels: {
        view: "Show Change Shift on team cards",
        create: "Apply Change Shift for reportee",
      },
      actionWhere: {
        view: "(My Team → team card ⋮ → Change Shift)",
        create: "(My Team → Change Shift modal — Submit applies immediately)",
      },
    },
    {
      key: "regularise_for_reportee",
      label: "Regularise checks for team",
      description:
        "Apply Regularise checks for a direct reportee from My Team (RM self-approval).",
      actions: ["view", "create"],
      actionLabels: {
        view: "Show Regularise on team cards",
        create: "Apply Regularise for reportee",
      },
      actionWhere: {
        view: "(My Team → team card ⋮ → Regularise checks)",
        create: "(My Team → Regularise modal — Submit applies immediately)",
      },
    },
    {
      key: "short_leave_for_reportee",
      label: "Short Leave for team",
      description:
        "Apply Short Leave for a direct reportee from My Team (RM self-approval).",
      actions: ["view", "create"],
      actionLabels: {
        view: "Show Short Leave on team cards",
        create: "Apply Short Leave for reportee",
      },
      actionWhere: {
        view: "(My Team → team card ⋮ → Short Leave)",
        create: "(My Team → Short Leave modal — Submit applies immediately)",
      },
    },
  ],
};

const ADMIN_CATALOG_SOURCE = [
  {
    key: "configuration",
    label: "Configuration",
    path: "/admin/configuration",
    description:
      "Configuration sections: Enterprise, Masters, and Settings. Attendance code mapping lives under Attendance → Settings → Codes and devices.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "enterprise",
        label: "Enterprise",
        description:
          "View and save the company/organization profile (Enterprise Details).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Company Profile",
          edit: "Edit / Save Company Details (incl. TAN, PAN, Head of Organisation)",
        },
        actionWhere: {
          view: "(Configuration → Enterprise — form page)",
          edit: "(Configuration → Enterprise — Edit / Save; TAN used by Form 24Q; Head of Organisation picker)",
        },
      },
      {
        key: "masters",
        label: "Masters",
        description:
          "Create, edit and deactivate Branch, Department, Designation, Sub-Department, Grade, Category and Reasons. When Minimum Wages is enabled, Category also maintains MW history.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Masters List",
          create: "Create New Master",
          edit: "Edit Master Record",
          delete: "Deactivate Master",
        },
        actionWhere: {
          view: "(tables)",
          create: "(toolbar - New button)",
          edit: "(table row - Edit)",
          delete: "(table row - Deactivate)",
        },
      },
      {
        key: "masters_import",
        label: "Import Masters",
        description:
          "Bulk import Enterprise masters (Branch, Department, Designation, Sub Department, Grade, Category, Reasons) from Excel templates with dropdown validation.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Open Import Masters",
          create: "Validate & Import Masters File",
        },
        actionWhere: {
          view: "(Import / Export → Configuration — Import Masters)",
          create: "(Import Masters modal — Validate & Import)",
        },
      },
      {
        key: "settings",
        label: "Settings",
        description:
          "Configuration → Settings hub. Nested rows grant Product theme and Organisation preferences.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Settings",
          edit: "Edit any Settings section",
        },
        actionWhere: {
          view: "(Configuration sidebar - Settings)",
          edit: "(Settings — Save)",
        },
      },
      {
        key: "config_set_product_theme",
        parentKey: "settings",
        label: "Product theme",
        description: "Configuration → Settings → Product theme color.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View product theme",
          edit: "Change product theme color",
        },
        actionWhere: {
          view: "(Configuration → Settings → Product theme)",
          edit: "(Product theme — Save)",
        },
      },
      {
        key: "config_set_org_preferences",
        parentKey: "settings",
        label: "Organisation preferences",
        description:
          "Configuration → Settings → Organisation preferences (pointers to Payroll defaults such as Minimum Wages / Default Salary Structure).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View organisation preferences",
          edit: "Edit organisation preference notes / links",
        },
        actionWhere: {
          view: "(Configuration → Settings → Organisation preferences)",
          edit: "(Organisation preferences)",
        },
      },
      {
        key: "attendance_codes",
        label: "Attendance Codes",
        description:
          "Configure import/export attendance marking codes (Present, LOP, Weekly Off, Holiday, etc.) under Attendance Settings.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Attendance Codes",
          edit: "Configure Codes",
        },
        actionWhere: {
          view: "(Attendance → Settings → Codes and devices — Import & export code mapping)",
          edit: "(Attendance → Settings → Codes and devices — Save mapping)",
        },
      },
      {
        key: "activity_console",
        label: "Action Hub",
        description:
          "Global Action Hub queue for employee / attendance / payroll request monitoring and desk actions.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "Open Action Hub",
          approve: "Approve / Reject pending desk items",
        },
        actionWhere: {
          view: "Admin rail — Action Hub",
          approve: "Action Hub detail — Approve / Reject",
        },
      },
    ],
  },
  {
    key: "employee",
    label: "Employee",
    path: "/admin/employee",
    description: "Employee directory and lifecycle (onboarding, exit).",
    actions: ["view", "create", "edit", "approve", "export"],
    screens: [
      // Order mirrors Employee left nav (Dashboard → Employee → Import → Reports → Export → Settings → Help → Insights).
      {
        key: "employee_dashboard",
        label: "Employee Dashboard",
        flowGroup: "Dashboard",
        description:
          "Employee module dashboard with headcount, summary widgets, and dashboard Excel/PDF export.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Employee Dashboard",
          export: "Export Workforce Dashboard (Excel / PDF)",
        },
        actionWhere: {
          view: "(left nav - Dashboard)",
          export: "(Dashboard → Excel / PDF buttons)",
        },
      },
      {
        key: "onboard_employee",
        label: "Onboard Employee",
        flowGroup: "Employee",
        description:
          "Onboarding workflow — draft candidate, candidate profile completion, policy approvals, and checklist tasks.",
        actions: ["view", "create", "approve"],
        actionLabels: {
          view: "View Onboarding Form",
          create: "Submit Onboarding / Create Candidate",
          approve: "Approve Onboarding Request (profile 100% required)",
        },
        actionWhere: {
          view: "(left nav - Onboard Employee)",
          create:
            "(Onboard form - Save Org with Reporting Manager → auto-routes to RM approval)",
          approve:
            "(Activity Console / Approvals — Approve; disabled until profile complete)",
        },
      },
      {
        key: "employee_list",
        label: "Employee List",
        flowGroup: "Employee",
        description: "Lists employees with search, filter, and bulk actions.",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Employee Table",
          create: "Import / Add New Employee",
          edit: "Edit Employee Record",
          export: "Export Employee List",
        },
        actionWhere: {
          view: "(Employee → Employees list; also Employees / Actions toggle → Employees)",
          create: "(toolbar - Create Employee / sidebar - Onboard Employee)",
          edit: "(Employees list row edit; Actions hub uses separate Employee → Actions screens)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "create_employee",
        label: "Create Employee",
        flowGroup: "Employee",
        description:
          "Direct employee creation without onboarding workflow — modal form with auto employee code.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Create Employee Modal",
          create: "Save New Employee",
        },
        actionWhere: {
          view: "(Employees list - Create Employee button)",
          create: "(modal - Org Details Save / final submit)",
        },
      },
      {
        key: "edit_employee",
        label: "Edit Employee (legacy)",
        flowGroup: "Legacy",
        description: "Updates existing employee profile and employment details.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Employee Details",
          edit: "Save Employee Changes",
        },
        actionWhere: {
          view: "(detail panel)",
          edit: "(Save button in form)",
        },
      },
      {
        key: "employee_profile",
        label: "Employee Profile",
        flowGroup: "Employee",
        description:
          "Open employee profile. Nested rows grant each profile tab (Org details through Documents).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open employee profile",
          edit: "Edit any profile tab (when nested tab not set)",
        },
        actionWhere: {
          view: "(employee profile panel)",
          edit: "(profile Save)",
        },
      },
      {
        key: "org_details",
        parentKey: "employee_profile",
        label: "Org Details",
        description: "Employee profile → Org Details tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Org Details", edit: "Edit Org Details" },
        actionWhere: { view: "(profile → Org Details)", edit: "(Save)" },
      },
      {
        key: "personal_details",
        parentKey: "employee_profile",
        label: "Personal",
        description: "Employee profile → Personal tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Personal", edit: "Edit Personal" },
        actionWhere: { view: "(profile → Personal)", edit: "(Save)" },
      },
      {
        key: "family_details",
        parentKey: "employee_profile",
        label: "Family",
        description: "Employee profile → Family tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Family", edit: "Edit Family" },
        actionWhere: { view: "(profile → Family)", edit: "(Save)" },
      },
      {
        key: "bank_details",
        parentKey: "employee_profile",
        label: "Bank",
        description: "Employee profile → Bank tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Bank", edit: "Edit Bank" },
        actionWhere: { view: "(profile → Bank)", edit: "(Save)" },
      },
      {
        key: "education_details",
        parentKey: "employee_profile",
        label: "Education",
        description: "Employee profile → Education tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Education", edit: "Edit Education" },
        actionWhere: { view: "(profile → Education)", edit: "(Save)" },
      },
      {
        key: "profession_details",
        parentKey: "employee_profile",
        label: "Profession",
        description: "Employee profile → Profession tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Profession", edit: "Edit Profession" },
        actionWhere: { view: "(profile → Profession)", edit: "(Save)" },
      },
      {
        key: "statutory_details",
        parentKey: "employee_profile",
        label: "Statutory Details",
        description: "Employee profile → Statutory Details tab.",
        actions: ["view", "edit"],
        actionLabels: { view: "View Statutory Details", edit: "Edit Statutory Details" },
        actionWhere: { view: "(profile → Statutory)", edit: "(Save)" },
      },
      {
        key: "profile_documents",
        parentKey: "employee_profile",
        label: "Documents",
        description: "Employee profile → Documents tab (uploads and downloads).",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Documents",
          create: "Upload Document",
          edit: "Edit Document",
          export: "Download / Export Documents",
        },
        actionWhere: {
          view: "(profile → Documents)",
          create: "(Upload)",
          edit: "(row menu)",
          export: "(Export)",
        },
      },
      {
        key: "employee_documents",
        label: "Documents (legacy)",
        flowGroup: "Legacy",
        description: "Legacy standalone Documents screen. Prefer Employee Profile → Documents.",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Documents",
          create: "Upload Document",
          edit: "Edit Document",
          export: "Download / Export Documents",
        },
        actionWhere: {
          view: "(documents tab)",
          create: "(toolbar - Upload button)",
          edit: "(row menu)",
          export: "(toolbar - Export button)",
        },
      },
      {
        key: "emp_act_view_employee",
        label: "View Employee",
        description:
          "Employees → Actions → View Employee: open the selected employee’s profile from the Actions hub.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show View Employee action",
          edit: "Open employee profile from Actions",
        },
        actionWhere: {
          view: "(Employee → Employees → Actions → View Employee)",
          edit: "(View Employee → Process / View)",
        },
      },
      {
        key: "emp_act_generate_letter",
        label: "Generate Letter",
        description:
          "Employees → Actions → Generate Letter: generate an employment letter for the selected employee.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Generate Letter action",
          create: "Generate letter for an employee",
        },
        actionWhere: {
          view: "(Employee → Employees → Actions → Generate Letter)",
          create: "(Generate Letter → Process / Generate)",
        },
      },
      {
        key: "emp_act_confirmation",
        label: "Confirmation",
        description:
          "Employees → Actions → Confirmation: run confirmation workflow for the selected employee.",
        actions: ["view", "create", "approve"],
        actionLabels: {
          view: "Show Confirmation action",
          create: "Start confirmation from Actions",
          approve: "Approve confirmation from Actions",
        },
        actionWhere: {
          view: "(Employee → Employees → Actions → Confirmation)",
          create: "(Confirmation → Process / Confirm)",
          approve: "(Confirmation workflow Approve)",
        },
      },
      {
        key: "emp_act_transfer",
        label: "Transfer",
        description:
          "Employees → Actions → Transfer: start transfer workflow for the selected employee.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Show Transfer action",
          create: "Start transfer from Actions",
          edit: "Update transfer from Actions",
        },
        actionWhere: {
          view: "(Employee → Employees → Actions → Transfer)",
          create: "(Transfer → Process / Transfer)",
          edit: "(Transfer modal)",
        },
      },
      {
        key: "emp_act_resignation",
        label: "Resignation",
        description:
          "Employees → Actions → Resignation: start resignation / separation for the selected employee.",
        actions: ["view", "create", "approve"],
        actionLabels: {
          view: "Show Resignation action",
          create: "Start resignation from Actions",
          approve: "Approve resignation from Actions",
        },
        actionWhere: {
          view: "(Employee → Employees → Actions → Resignation)",
          create: "(Resignation → Process / Resign)",
          approve: "(Resignation / Separation Approve)",
        },
      },
      {
        key: "employee_import",
        label: "Import Employee Details",
        description:
          "Bulk import employee data. Grant nested sections for Org, Personal, Family, Bank, Qualification, Professional, Statutory.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Open Import Employee Details",
          create: "Upload any import section",
        },
        actionWhere: {
          view: "Employee → Import",
          create: "Import → Upload",
        },
      },
      {
        key: "employee_bulk_exit",
        label: "Bulk Employees Exit",
        flowGroup: "Import",
        description:
          "Bulk exit existing employees (Relieved / Terminated) via Import / Export Excel, with Masters reasons and attendance/payroll month locks.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Bulk Exit Import",
          create: "Validate / Confirm Bulk Exit Import",
        },
        actionWhere: {
          view: "(Import / Export → Employee — Bulk Employees Exit)",
          create: "(Bulk Employees Exit dialog — Validate / Import)",
        },
      },
      {
        key: "employee_reports",
        label: "Employee Reports",
        description:
          "Employee reports hub. Grant nested reports for Employees, Age, Service, Salary, Confirmation, Transfers, Resignations.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Employee Reports",
          export: "Download any employee report",
        },
        actionWhere: {
          view: "Employee → Reports",
          export: "Reports → Download",
        },
      },
      {
        key: "employee_export",
        label: "Export Employee Details",
        description:
          "Export employee data. Grant nested sections for Org, Personal, Family, Bank, Educational, Professional, Statutory, All Data.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Export Employee Details",
          export: "Download any export section",
        },
        actionWhere: {
          view: "Employee → Export",
          export: "Export → Download",
        },
      },
      {
        key: "employee_settings",
        label: "General Settings",
        description:
          "Employee → Settings → General Settings (ESS flags). Nested rows grant Employee Code, Policies, and Letters.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open General Settings",
          edit: "Save General Settings",
        },
        actionWhere: {
          view: "Employee → Settings → General Settings",
          edit: "Save",
        },
      },
      {
        key: "emp_set_employee_code",
        parentKey: "employee_settings",
        label: "Employee Code",
        description: "Employee → Settings → Employee Code structure.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Employee Code settings", edit: "Save Employee Code pattern" },
        actionWhere: { view: "Employee → Settings → Employee Code", edit: "Save" },
      },
      {
        key: "emp_set_policies",
        parentKey: "employee_settings",
        label: "Policies",
        description: "Employee → Settings → Policies hub.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Policies settings", edit: "Edit any policy section" },
        actionWhere: { view: "Employee → Settings → Policies", edit: "Save policy" },
      },
      {
        key: "emp_set_policy_onboarding",
        parentKey: "emp_set_policies",
        label: "Onboarding Policy",
        description: "Settings → Policies → Onboarding.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Onboarding policy", edit: "Save Onboarding policy" },
        actionWhere: { view: "Employee → Settings → Policies → Onboarding", edit: "Save" },
      },
      {
        key: "emp_set_policy_confirmation",
        parentKey: "emp_set_policies",
        label: "Confirmation Policy",
        description: "Settings → Policies → Confirmation.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Confirmation policy", edit: "Save Confirmation policy" },
        actionWhere: { view: "Employee → Settings → Policies → Confirmation", edit: "Save" },
      },
      {
        key: "emp_set_policy_transfer",
        parentKey: "emp_set_policies",
        label: "Transfer Policy",
        description: "Settings → Policies → Transfer.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Transfer policy", edit: "Save Transfer policy" },
        actionWhere: { view: "Employee → Settings → Policies → Transfer", edit: "Save" },
      },
      {
        key: "emp_set_policy_separation",
        parentKey: "emp_set_policies",
        label: "Separation Policy",
        description: "Settings → Policies → Separation.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Separation policy", edit: "Save Separation policy" },
        actionWhere: { view: "Employee → Settings → Policies → Separation", edit: "Save" },
      },
      {
        key: "letter_offer",
        parentKey: "letters_management",
        label: "Offer Letter",
        description: "Letters Management → Offer Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Offer Letter templates",
          create: "Add Offer Letter template",
          edit: "Edit Offer Letter template",
          delete: "Delete Offer Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Offer Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_appointment",
        parentKey: "letters_management",
        label: "Appointment Letter",
        description: "Letters Management → Appointment Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Appointment Letter templates",
          create: "Add Appointment Letter template",
          edit: "Edit Appointment Letter template",
          delete: "Delete Appointment Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Appointment Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_transfer",
        parentKey: "letters_management",
        label: "Transfer Letter",
        description: "Letters Management → Transfer Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Transfer Letter templates",
          create: "Add Transfer Letter template",
          edit: "Edit Transfer Letter template",
          delete: "Delete Transfer Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Transfer Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_confirmation",
        parentKey: "letters_management",
        label: "Confirmation Letter",
        description: "Letters Management → Confirmation Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Confirmation Letter templates",
          create: "Add Confirmation Letter template",
          edit: "Edit Confirmation Letter template",
          delete: "Delete Confirmation Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Confirmation Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_experience",
        parentKey: "letters_management",
        label: "Experience Letter",
        description: "Letters Management → Experience Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Experience Letter templates",
          create: "Add Experience Letter template",
          edit: "Edit Experience Letter template",
          delete: "Delete Experience Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Experience Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_resignation",
        parentKey: "letters_management",
        label: "Resignation Acceptance Letter",
        description: "Letters Management → Resignation Acceptance Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Resignation Acceptance Letter templates",
          create: "Add Resignation Acceptance Letter template",
          edit: "Edit Resignation Acceptance Letter template",
          delete: "Delete Resignation Acceptance Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Resignation Acceptance Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_promotion",
        parentKey: "letters_management",
        label: "Promotion Letter",
        description: "Letters Management → Promotion Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Promotion Letter templates",
          create: "Add Promotion Letter template",
          edit: "Edit Promotion Letter template",
          delete: "Delete Promotion Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Promotion Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_salary_increment",
        parentKey: "letters_management",
        label: "Salary Increment Letter",
        description: "Letters Management → Salary Increment Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Salary Increment Letter templates",
          create: "Add Salary Increment Letter template",
          edit: "Edit Salary Increment Letter template",
          delete: "Delete Salary Increment Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Salary Increment Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_probation_extension",
        parentKey: "letters_management",
        label: "Probation Extension Letter",
        description: "Letters Management → Probation Extension Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Probation Extension Letter templates",
          create: "Add Probation Extension Letter template",
          edit: "Edit Probation Extension Letter template",
          delete: "Delete Probation Extension Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Probation Extension Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_termination",
        parentKey: "letters_management",
        label: "Termination Letter",
        description: "Letters Management → Termination Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Termination Letter templates",
          create: "Add Termination Letter template",
          edit: "Edit Termination Letter template",
          delete: "Delete Termination Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Termination Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_contract",
        parentKey: "letters_management",
        label: "Contract Letter",
        description: "Letters Management → Contract Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Contract Letter templates",
          create: "Add Contract Letter template",
          edit: "Edit Contract Letter template",
          delete: "Delete Contract Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Contract Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_contract_renewal",
        parentKey: "letters_management",
        label: "Contract Renewal Letter",
        description: "Letters Management → Contract Renewal Letter.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Contract Renewal Letter templates",
          create: "Add Contract Renewal Letter template",
          edit: "Edit Contract Renewal Letter template",
          delete: "Delete Contract Renewal Letter template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Contract Renewal Letter",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letter_other",
        parentKey: "letters_management",
        label: "Other Company Letters",
        description: "Letters Management → Other Company Letters.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Open Other Company Letters templates",
          create: "Add Other Company Letters template",
          edit: "Edit Other Company Letters template",
          delete: "Delete Other Company Letters template",
        },
        actionWhere: {
          view: "Employee → Settings → Letters → Other Company Letters",
          create: "Add template",
          edit: "Edit template",
          delete: "Delete template",
        },
      },
      {
        key: "letters_management",
        parentKey: "employee_settings",
        label: "Letters Management",
        description:
          "Letters hub. Grant nested letter types for template access (offer, appointment, etc.).",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Letters Management",
          create: "Upload / Add Letter Template",
          edit: "Edit Letter Template",
          delete: "Delete Letter Template",
        },
        actionWhere: {
          view: "(Employee → Settings → Letters Management; Employees list → row menu → Generate Letter)",
          create: "(Letters Management - Add Template Format; Generate Letter - Preview / Download PDF / Send mail)",
          edit: "(Letters Management - Edit / Upload)",
          delete: "(Letters Management - Delete)",
        },
      },
      {
        key: "emp_action_hub",
        label: "Action Hub",
        description: "Open Action Hub filtered to employee lifecycle request monitoring.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "Open Employee Action Hub",
          approve: "Approve / Reject employee desk items",
        },
        actionWhere: { view: "Action Hub · Employee", approve: "Action Hub detail" },
      },
      {
        key: "emp_imp_org",
        parentKey: "employee_import",
        label: "Import Org Details",
        description: "Import → Employee Details → Org Details.",
        actions: ["view", "create"],
        actionLabels: { view: "See Org Details import", create: "Upload Org Details" },
        actionWhere: { view: "Employee → Import → Org Details", create: "Upload" },
      },
      {
        key: "emp_imp_personal",
        parentKey: "employee_import",
        label: "Import Personal Details",
        description: "Import → Employee Details → Personal Details.",
        actions: ["view", "create"],
        actionLabels: { view: "See Personal Details import", create: "Upload Personal Details" },
        actionWhere: { view: "Employee → Import → Personal", create: "Upload" },
      },
      {
        key: "emp_imp_family",
        parentKey: "employee_import",
        label: "Import Family Details",
        description: "Import → Employee Details → Family Details.",
        actions: ["view", "create"],
        actionLabels: { view: "See Family Details import", create: "Upload Family Details" },
        actionWhere: { view: "Employee → Import → Family", create: "Upload" },
      },
      {
        key: "emp_imp_bank",
        parentKey: "employee_import",
        label: "Import Bank Details",
        description: "Import → Employee Details → Bank Details.",
        actions: ["view", "create"],
        actionLabels: { view: "See Bank Details import", create: "Upload Bank Details" },
        actionWhere: { view: "Employee → Import → Bank", create: "Upload" },
      },
      {
        key: "emp_imp_qualification",
        parentKey: "employee_import",
        label: "Import Qualification Details",
        description: "Import → Employee Details → Qualification.",
        actions: ["view", "create"],
        actionLabels: { view: "See Qualification import", create: "Upload Qualification" },
        actionWhere: { view: "Employee → Import → Qualification", create: "Upload" },
      },
      {
        key: "emp_imp_professional",
        parentKey: "employee_import",
        label: "Import Professional Details",
        description: "Import → Employee Details → Professional.",
        actions: ["view", "create"],
        actionLabels: { view: "See Professional import", create: "Upload Professional" },
        actionWhere: { view: "Employee → Import → Professional", create: "Upload" },
      },
      {
        key: "emp_imp_statutory",
        parentKey: "employee_import",
        label: "Import Statutory Details",
        description: "Import → Employee Details → Statutory.",
        actions: ["view", "create"],
        actionLabels: { view: "See Statutory import", create: "Upload Statutory" },
        actionWhere: { view: "Employee → Import → Statutory", create: "Upload" },
      },
      {
        key: "emp_exp_org",
        parentKey: "employee_export",
        label: "Export Org Details",
        description: "Export → Org Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Org Details export", export: "Download Org Details" },
        actionWhere: { view: "Employee → Export → Org", export: "Download" },
      },
      {
        key: "emp_exp_personal",
        parentKey: "employee_export",
        label: "Export Personal Details",
        description: "Export → Personal Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Personal Details export", export: "Download Personal Details" },
        actionWhere: { view: "Employee → Export → Personal", export: "Download" },
      },
      {
        key: "emp_exp_family",
        parentKey: "employee_export",
        label: "Export Family Details",
        description: "Export → Family Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Family Details export", export: "Download Family Details" },
        actionWhere: { view: "Employee → Export → Family", export: "Download" },
      },
      {
        key: "emp_exp_bank",
        parentKey: "employee_export",
        label: "Export Bank Details",
        description: "Export → Bank Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Bank Details export", export: "Download Bank Details" },
        actionWhere: { view: "Employee → Export → Bank", export: "Download" },
      },
      {
        key: "emp_exp_educational",
        parentKey: "employee_export",
        label: "Export Educational Details",
        description: "Export → Educational Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Educational export", export: "Download Educational" },
        actionWhere: { view: "Employee → Export → Educational", export: "Download" },
      },
      {
        key: "emp_exp_professional",
        parentKey: "employee_export",
        label: "Export Professional Details",
        description: "Export → Professional Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Professional export", export: "Download Professional" },
        actionWhere: { view: "Employee → Export → Professional", export: "Download" },
      },
      {
        key: "emp_exp_statutory",
        parentKey: "employee_export",
        label: "Export Statutory Details",
        description: "Export → Statutory Details.",
        actions: ["view", "export"],
        actionLabels: { view: "See Statutory export", export: "Download Statutory" },
        actionWhere: { view: "Employee → Export → Statutory", export: "Download" },
      },
      {
        key: "emp_exp_all",
        parentKey: "employee_export",
        label: "Export All Data",
        description: "Export → All employee data.",
        actions: ["view", "export"],
        actionLabels: { view: "See All Data export", export: "Download All Data" },
        actionWhere: { view: "Employee → Export → All", export: "Download" },
      },
      {
        key: "emp_rpt_employees",
        parentKey: "employee_reports",
        label: "Employees Report",
        description: "Employee → Reports → Employees.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Employees report", export: "Download Employees report" },
        actionWhere: { view: "Employee → Reports → Employees", export: "Download" },
      },
      {
        key: "emp_rpt_age",
        parentKey: "employee_reports",
        label: "Age Report",
        description: "Employee → Reports → Age.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Age report", export: "Download Age report" },
        actionWhere: { view: "Employee → Reports → Age", export: "Download" },
      },
      {
        key: "emp_rpt_service",
        parentKey: "employee_reports",
        label: "Service Report",
        description: "Employee → Reports → Service.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Service report", export: "Download Service report" },
        actionWhere: { view: "Employee → Reports → Service", export: "Download" },
      },
      {
        key: "emp_rpt_salary",
        parentKey: "employee_reports",
        label: "Salary Report",
        description: "Employee → Reports → Salary.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Salary report", export: "Download Salary report" },
        actionWhere: { view: "Employee → Reports → Salary", export: "Download" },
      },
      {
        key: "emp_rpt_confirmation",
        parentKey: "employee_reports",
        label: "Confirmation Report",
        description: "Employee → Reports → Confirmation.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Confirmation report", export: "Download Confirmation report" },
        actionWhere: { view: "Employee → Reports → Confirmation", export: "Download" },
      },
      {
        key: "emp_rpt_transfers",
        parentKey: "employee_reports",
        label: "Transfers Report",
        description: "Employee → Reports → Transfers.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Transfers report", export: "Download Transfers report" },
        actionWhere: { view: "Employee → Reports → Transfers", export: "Download" },
      },
      {
        key: "emp_rpt_resignations",
        parentKey: "employee_reports",
        label: "Resignations Report",
        description: "Employee → Reports → Resignations.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Resignations report", export: "Download Resignations report" },
        actionWhere: { view: "Employee → Reports → Resignations", export: "Download" },
      },
    ],
  },
  {
    key: "attendance",
    label: "Attendance",
    path: "/admin/attendance",
    description: "Daily attendance, regularization and leave requests.",
    actions: ["view", "create", "edit", "approve", "export"],
    screens: [
      {
        key: "attendance_dashboard",
        label: "Attendance Dashboard",
        description:
          "Shows attendance KPIs, trends, monthly summaries, and dashboard Excel/PDF export (left nav Dashboard).",
        actions: ["view", "create", "edit", "export"],
        actionLabels: {
          view: "View Attendance Dashboard",
          create: "Mark All Attendance",
          edit: "Lock/Unlock Attendance",
          export: "Export Attendance Dashboard (Excel / PDF)",
        },
        actionWhere: {
          view: "(Attendance → Dashboard)",
          create: "(Bulk Actions → Mark All page)",
          edit: "(Process Attendance → Lock / Request Unlock)",
          export: "(Dashboard → Excel / PDF buttons; left nav → Export page)",
        },
      },
      {
        key: "mark_attendance",
        label: "Mark Attendance",
        description: "Marks and edits day-wise employee attendance records.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Attendance Grid",
          create: "Mark Attendance",
          edit: "Edit Attendance Record",
        },
        actionWhere: {
          view: "(Attendance Register / Bulk Mark grid)",
          create: "(inline cell edit / Bulk Actions → Mark All page)",
          edit:
            "(Bulk Mark / Register cell edits; calendar day edits when Change Status action is also granted)",
        },
      },
      {
        key: "attendance_checklist",
        label: "Attendance Checklist",
        description:
          "Define checklist templates for Process Attendance (sidebar item above Process Attendance).",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Attendance Checklist editor",
          create: "Create checklist template",
          edit: "Edit or delete checklist templates",
        },
        actionWhere: {
          view: "(Attendance → Checklist)",
          create: "(Checklist → New checklist)",
          edit: "(Checklist → Save / Delete)",
        },
      },
      {
        key: "process_attendance",
        label: "Open Process wizard",
        description:
          "Opens the Attendance Process wizard. Auto-enabled when any Process step below is granted; keep View on so the tab appears.",
        actions: ["view"],
        actionLabels: {
          view: "Open Process Attendance",
        },
        actionWhere: {
          view: "(Attendance → Process Attendance)",
        },
      },
      {
        key: "att_proc_reconciliation",
        label: "Reconciliation",
        description: "Review leave and LOP headcounts for the month.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Reconciliation step",
          edit: "Validate / update Reconciliation",
        },
        actionWhere: {
          view: "(Attendance → Process → Reconciliation)",
          edit: "(Reconciliation → Validate)",
        },
      },
      {
        key: "att_proc_process",
        label: "Process Attendance",
        description:
          "View opens the step. Edit shows Process / Reprocess / Unprocess. Quick reports are separate nested grants under this step.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Process Attendance step",
          edit: "Show Process / Reprocess / Unprocess buttons",
        },
        actionWhere: {
          view: "Attendance → Process → Process Attendance",
          edit: "Process step footer · Process / Reprocess / Unprocess",
        },
      },
      {
        key: "att_proc_checklist",
        label: "Checklist",
        description: "Complete month checklist tasks defined under Attendance Checklist.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Checklist step",
          edit: "Mark checklist items complete",
        },
        actionWhere: {
          view: "(Attendance → Process → Checklist)",
          edit: "(Checklist step)",
        },
      },
      {
        key: "att_proc_preview_validate",
        label: "Preview & Validate",
        description: "Summary cards for all steps plus exceptions.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Preview & Validate step",
          edit: "Validate preview",
        },
        actionWhere: {
          view: "(Attendance → Process → Preview & Validate)",
          edit: "(Preview & Validate → Validate)",
        },
      },
      {
        key: "att_proc_finalise",
        label: "Finalise Attendance",
        description: "Lock the attendance month so payroll can consume paid days.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "Open Finalise step",
          approve: "Finalise / unlock attendance month",
        },
        actionWhere: {
          view: "(Attendance → Process → Finalise)",
          approve: "(Finalise → Validate / Unlock)",
        },
      },
      {
        key: "att_act_change_status",
        label: "Change Status",
        description:
          "Process → Actions → Change Status: open an employee’s attendance calendar to view or change day status.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Change Status action",
          edit: "Open calendar and change day status",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Change Status)",
          edit: "(Change Status → Process / Open → calendar day status)",
        },
      },
      {
        key: "att_act_apply_leave",
        label: "Apply Leave",
        description:
          "Process → Actions → Apply Leave: raise leave for a selected employee from Attendance Actions.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Apply Leave action",
          create: "Apply leave for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Apply Leave)",
          create: "(Apply Leave → Process / Apply)",
        },
      },
      {
        key: "att_act_change_shift",
        label: "Change Shift",
        description:
          "Process → Actions → Change Shift: change shift for a selected employee.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Change Shift action",
          edit: "Submit Change Shift for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Change Shift)",
          edit: "(Change Shift → Process / Change)",
        },
      },
      {
        key: "att_act_change_wo",
        label: "Change WO",
        description:
          "Process → Actions → Change WO: change weekly off for a selected employee (respects attendance locks).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Change WO action",
          edit: "Submit Change WO for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Change WO)",
          edit: "(Change WO → Process / Change)",
        },
      },
      {
        key: "att_act_regularise_checks",
        label: "Regularise Checks",
        description:
          "Process → Actions → Regularise checks: raise regularisation for a selected employee’s punches.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Regularise checks action",
          create: "Raise regularisation for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Regularise checks)",
          create: "(Regularise checks → Process / Regularise)",
        },
      },
      {
        key: "att_act_short_leave",
        label: "Short Leave",
        description:
          "Process → Actions → Short Leave: apply short leave for a selected employee.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Short Leave action",
          create: "Apply short leave for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Short Leave)",
          create: "(Short Leave → Process / Apply)",
        },
      },
      {
        key: "att_act_lop_reversal",
        label: "LOP Reversal",
        description:
          "Process → Actions → LOP Reversal: reverse LOP/absent for a selected employee (may be allowed in locked months).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show LOP Reversal action",
          edit: "Save LOP reversal for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → LOP Reversal)",
          edit: "(LOP Reversal → Process / Reverse)",
        },
      },
      {
        key: "att_act_leave_adjustment",
        label: "Leave Adjustment",
        description:
          "Process → Actions → Leave Adjustment: adjust leave balance for a selected employee.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Leave Adjustment action",
          edit: "Save leave adjustment for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Leave Adjustment)",
          edit: "(Leave Adjustment → Process / Adjust)",
        },
      },
      {
        key: "att_act_leave_balance",
        label: "Leave Balance",
        description:
          "Process → Actions → Leave Balance: view leave balances for a selected employee.",
        actions: ["view"],
        actionLabels: {
          view: "Show Leave Balance action and open balances",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Leave Balance)",
        },
      },
      {
        key: "att_act_comp_off_ledger",
        label: "Comp Off Ledger",
        description:
          "Process → Actions → Comp Off Ledger: open Comp Off lots for a selected employee. Company-wide report is under Reports → Comp Off Ledger Report.",
        actions: ["view"],
        actionLabels: {
          view: "Show Comp Off Ledger action and open employee ledger",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Comp Off Ledger)",
        },
      },
      {
        key: "view_edit_checks",
        label: "View and Edit Checks",
        description:
          "Process → Actions → View and Edit Checks: view, add, edit, or delete employee punch checks (web, biometric, mobile, regularise, manual). Recalculates day status immediately. Blocked when the attendance month is finalised.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "Show View and Edit Checks action",
          create: "Add manual check",
          edit: "Edit check time or kind",
          delete: "Delete check",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → View and Edit Checks; calendar → ⋮ → View and Edit Checks)",
          create: "(View and Edit Checks → Add check)",
          edit: "(View and Edit Checks → ⋮ Edit)",
          delete: "(View and Edit Checks → ⋮ Delete)",
        },
      },
      {
        key: "att_act_on_duty",
        label: "On Duty",
        description:
          "Process → Actions → On Duty: raise On Duty for a selected employee.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show On Duty action",
          create: "Raise On Duty for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → On Duty)",
          create: "(On Duty → Process / Open)",
        },
      },
      {
        key: "att_act_overtime",
        label: "Overtime",
        description:
          "Process → Actions → Overtime: raise Overtime for a selected employee.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Overtime action",
          create: "Raise Overtime for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Overtime)",
          create: "(Overtime → Process / Open)",
        },
      },
      {
        key: "att_act_restricted_holiday",
        label: "Restricted Holiday",
        description:
          "Process → Actions → Restricted Holiday: raise Restricted Holiday for a selected employee.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Show Restricted Holiday action",
          create: "Raise Restricted Holiday for an employee",
        },
        actionWhere: {
          view: "(Attendance → Process → Actions → Restricted Holiday)",
          create: "(Restricted Holiday → Process / Open)",
        },
      },
      {
        key: "attendance_import_checks",
        label: "Import Checks",
        description:
          "Bulk import punch checks from Excel (append or replace day). Validates IN/OUT sequence, locked months, approved leave, and Comp Off. Admin-only; Import / Export hub.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Open Import Checks",
          create: "Validate and import checks file",
        },
        actionWhere: {
          view: "(Import / Export → Attendance → Import Checks)",
          create: "(Import Checks → Validate / Confirm import)",
        },
      },
      {
        key: "attendance_general_settings",
        label: "General Settings",
        description:
          "Attendance → Settings → General Settings: schedule recalculation and notify roles.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open General Settings",
          edit: "Save schedule and notify roles",
        },
        actionWhere: {
          view: "(Attendance → Settings → General Settings)",
          edit: "(General Settings → Save settings)",
        },
      },
      {
        key: "attendance_policies",
        label: "Policies",
        description: "Attendance → Settings → Policies hub.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Policies settings", edit: "Edit any attendance policy" },
        actionWhere: { view: "Attendance → Settings → Policies", edit: "Save" },
      },
      {
        key: "attendance_codes_devices",
        label: "Codes and devices",
        description: "Attendance → Settings → Codes and devices hub.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Codes and devices", edit: "Edit codes or devices" },
        actionWhere: { view: "Attendance → Settings → Codes and devices", edit: "Save" },
      },
      {
        key: "attendance_weekly_off_settings",
        parentKey: "attendance_policies",
        label: "Weekly Off Settings",
        description:
          "Attendance → Settings → Policies → Weekly Off: create and manage weekly-off policies.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Weekly Off settings",
          create: "Create Weekly Off policy",
          edit: "Edit / delete Weekly Off policy",
        },
        actionWhere: {
          view: "(Attendance → Settings → Policies → Weekly Off)",
          create: "(Weekly Off → Create Weekly Off Policy)",
          edit: "(Weekly Off → ⋮ Edit / Delete)",
        },
      },
      {
        key: "attendance_holiday_settings",
        parentKey: "attendance_policies",
        label: "Holiday Settings",
        description:
          "Attendance → Settings → Policies → Holiday: holiday bank and holiday policies (incl. Restricted Holiday).",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Holiday settings",
          create: "Create Holiday policy / bank entry",
          edit: "Edit / delete Holiday policy",
        },
        actionWhere: {
          view: "(Attendance → Settings → Policies → Holiday)",
          create: "(Holiday → Create Holiday Policy)",
          edit: "(Holiday → ⋮ Edit / Delete)",
        },
      },
      {
        key: "attendance_leave_settings",
        parentKey: "attendance_policies",
        label: "Leave Management Settings",
        description:
          "Attendance → Settings → Policies → Leave Management: leave types and leave groups.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Leave Management settings",
          create: "Create leave type / group",
          edit: "Edit leave type / group",
        },
        actionWhere: {
          view: "(Attendance → Settings → Policies → Leave Management)",
          create: "(Leave Management → Create)",
          edit: "(Leave Management → Edit)",
        },
      },
      {
        key: "attendance_shift_settings",
        parentKey: "attendance_policies",
        label: "Shift Settings",
        description:
          "Attendance → Settings → Policies → Shift: create and manage shifts.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Shift settings",
          create: "Create shift",
          edit: "Edit shift",
        },
        actionWhere: {
          view: "(Attendance → Settings → Policies → Shift)",
          create: "(Shift → Create shift)",
          edit: "(Shift → Edit)",
        },
      },
      {
        key: "attendance_policy_settings",
        parentKey: "attendance_policies",
        label: "Attendance Policy Settings",
        description:
          "Attendance → Settings → Policies → Attendance Policy: punch-day assignment, per-activity workflows (Short Leave, Regularise, OD, Comp Off, OT, RH), Manual Override path.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open Attendance Policy settings",
          create: "Create attendance policy",
          edit: "Edit attendance policy",
        },
        actionWhere: {
          view: "(Attendance → Settings → Policies → Attendance Policy)",
          create: "(+ New attendance policy)",
          edit: "(policy ⋯ → Edit)",
        },
      },
      {
        key: "attendance_code_mapping",
        parentKey: "attendance_codes_devices",
        label: "Import & Export Code Mapping",
        description:
          "Attendance → Settings → Codes and devices → Import & export code mapping: map PR/LOP/WO/PH codes for Excel import/export.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open code mapping",
          edit: "Save mapping / Import CSV / half-day format",
        },
        actionWhere: {
          view: "(Attendance → Settings → Codes and devices → Import & export code mapping)",
          edit: "(Code mapping → Save / Import CSV)",
        },
      },
      {
        key: "attendance_devices",
        parentKey: "attendance_codes_devices",
        label: "Biometric & API Devices",
        description:
          "Attendance → Settings → Codes and devices → Biometric & API devices: configure device integrations.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Open devices settings",
          create: "Add biometric / API device",
          edit: "Edit device configuration",
        },
        actionWhere: {
          view: "(Attendance → Settings → Codes and devices → Biometric & API devices)",
          create: "(Devices → Add)",
          edit: "(Devices → Edit)",
        },
      },
      {
        key: "attendance_run_engine",
        label: "Run Attendance Engine",
        description:
          "Older engine grant. Prefer Process → Process Attendance Edit. Engine sync runs with Process / Reprocess / Unprocess.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "See process actions that run the engine",
          edit: "Run Process / Reprocess / Unprocess with engine sync",
        },
        actionWhere: {
          view: "(Process → Process Attendance)",
          edit: "(Process → Process Attendance · Edit)",
        },
      },
      {
        key: "att_rpt_register",
        parentKey: "att_proc_process",
        label: "Attendance Register",
        description: "Process Attendance → Quick reports → Attendance Register Excel.",
        actions: ["view", "export"],
        actionLabels: {
          view: "See Attendance Register in Quick reports",
          export: "Download Attendance Register Excel",
        },
        actionWhere: {
          view: "Attendance → Process → Quick reports",
          export: "Quick reports → Attendance Register",
        },
      },
      {
        key: "attendance_history",
        label: "Attendance History",
        description: "Displays historical attendance logs and exportable data.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Attendance History",
          export: "Export History Report",
        },
        actionWhere: {
          view: "Attendance History page",
          export: "Export button",
        },
      },
      {
        key: "attendance_import",
        label: "Bulk Import",
        description: "Imports attendance data from template or file upload.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Import Interface",
          create: "Upload & Import Attendance",
        },
        actionWhere: {
          view: "(Import → Bulk Import Attendance page)",
          create: "(Import page - Upload button; only unfinalised months)",
        },
      },
      {
        key: "comp_off_opening_import",
        label: "Comp Off Opening Balance Import",
        description:
          "Import pre go-live Comp Off lots (worked date, credit days, expiry) under Attendance → Import.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Open Comp Off opening import",
          create: "Validate and import Comp Off opening lots",
        },
        actionWhere: {
          view: "(Attendance → Import → Comp Off Opening Balance)",
          create: "(Import → Comp Off Opening Balance → Validate & preview / Import)",
        },
      },
      {
        key: "attendance_lop_reversal_import",
        label: "Bulk LOP Reversal Import",
        description:
          "Import LOP reversals from Excel (employee, LOP date, new status, payroll month) under Import / Export → Attendance.",
        actions: ["view", "create"],
        actionLabels: {
          view: "Open LOP Reversal Import",
          create: "Validate and import LOP reversal rows",
        },
        actionWhere: {
          view: "(Import / Export → Attendance → Bulk LOP Reversal)",
          create: "(Import → Bulk LOP Reversal → Validate & preview / Import)",
        },
      },
      {
        key: "comp_off_ledger_report",
        label: "Comp Off Ledger Report",
        description:
          "Company-wide Comp Off earn and avail ledger with worked date, expiry, availed date, and remaining days.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Comp Off Ledger Report",
          export: "Export Comp Off Ledger Excel",
        },
        actionWhere: {
          view: "(Attendance → Reports → Comp Off Ledger Report)",
          export: "(Comp Off Ledger Report → Download)",
        },
      },
      {
        key: "att_action_hub",
        label: "Action Hub",
        description: "Open Action Hub filtered to attendance request monitoring.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "Open Attendance Action Hub",
          approve: "Approve / Reject attendance desk items",
        },
        actionWhere: {
          view: "Action Hub · Attendance",
          approve: "Action Hub detail",
        },
      },
      {
        key: "att_rpt_checks",
        label: "Attendance Checks",
        description: "Attendance → Reports → Attendance Checks.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Attendance Checks report", export: "Export Attendance Checks" },
        actionWhere: { view: "Attendance → Reports → Attendance Checks", export: "Export" },
      },
      {
        key: "att_rpt_daily",
        label: "Daily Attendance Report",
        description: "Attendance → Reports → Daily Attendance Report.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Daily Attendance Report", export: "Export Daily Attendance" },
        actionWhere: { view: "Attendance → Reports → Daily Attendance", export: "Export" },
      },
      {
        key: "att_rpt_who_are_in",
        label: "Who are in",
        description: "Attendance → Reports → Who are in.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Who are in report", export: "Export Who are in" },
        actionWhere: { view: "Attendance → Reports → Who are in", export: "Export" },
      },
      {
        key: "att_rpt_monthly",
        label: "Monthly Attendance Report",
        description: "Attendance → Reports → Monthly Attendance Report.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Monthly Attendance Report", export: "Export Monthly Attendance" },
        actionWhere: { view: "Attendance → Reports → Monthly Attendance", export: "Export" },
      },
      {
        key: "att_rpt_leave_balance",
        label: "Leave Balance Report",
        description: "Attendance → Reports → Leave Balance Report.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Leave Balance Report", export: "Export Leave Balance" },
        actionWhere: { view: "Attendance → Reports → Leave Balance", export: "Export" },
      },
      {
        key: "att_rpt_overtime",
        label: "Overtime Report",
        description: "Attendance → Reports → Overtime Report.",
        actions: ["view", "export"],
        actionLabels: { view: "Open Overtime Report", export: "Export Overtime" },
        actionWhere: { view: "Attendance → Reports → Overtime", export: "Export" },
      },
      {
        key: "att_rpt_lop_reversal",
        label: "LOP Reversal Report",
        description: "Attendance → Reports → LOP Reversal Report.",
        actions: ["view", "export"],
        actionLabels: { view: "Open LOP Reversal Report", export: "Export LOP Reversal" },
        actionWhere: { view: "Attendance → Reports → LOP Reversal", export: "Export" },
      },
    ],
  },
  {
    key: "payroll",
    label: "Payroll",
    path: "/admin/payroll",
    description: "Salary structures, processing and payslips.",
    actions: ["view", "create", "edit", "delete", "approve", "export"],
    screens: [
      {
        key: "payroll_dashboard",
        label: "Payroll Dashboard",
        description:
          "Payroll cost, cash outflow, tax/statutory analytics, master-wise and period comparison.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Payroll Dashboard",
          export: "Export Payroll Dashboard (Excel / PDF)",
        },
        actionWhere: {
          view: "(Payroll → Dashboard)",
          export: "(Payroll → Dashboard · Excel / PDF)",
        },
      },
      {
        key: "apply_auto_structures",
        label: "Apply Auto Structures",
        description:
          "Bulk-save the matched Auto (Formula) salary structure for employees with no saved structure (e.g. imported before the structure was configured). Lists short CTC / no match / Manual reasons for the rest.",
        actions: ["create"],
        actionLabels: {
          create: "Apply Auto Structures to employees without a saved structure",
        },
        actionWhere: {
          create: "(Payroll → Dashboard → Alerts - Salary structure not saved - Apply Auto Structures)",
        },
      },
      {
        key: "pay_act_assigned_structure",
        label: "Assigned Structure",
        description:
          "Run Payroll → Actions → Assigned Structure: view or edit the salary structure assigned to an employee.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Assigned Structure action",
          edit: "Open / edit assigned structure from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Assigned Structure)",
          edit: "(Assigned Structure → Process / Structure)",
        },
      },
      {
        key: "pay_act_salary_revision",
        label: "Salary Revision",
        description:
          "Run Payroll → Actions → Salary Revision: create or review salary revision for an employee.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Salary Revision action",
          edit: "Open salary revision from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Salary Revision)",
          edit: "(Salary Revision → Process / Revise)",
        },
      },
      {
        key: "pay_act_update_statutory",
        label: "Update Statutory",
        description:
          "Run Payroll → Actions → Update Statutory: update PF, ESI, PT, NPS, VPF, Meal and related applicability flags.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Update Statutory action",
          edit: "Save statutory applicability from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Update Statutory)",
          edit: "(Update Statutory → Process / Update)",
        },
      },
      {
        key: "pay_act_tax_planning",
        label: "Tax Planning",
        description:
          "Run Payroll → Actions → Tax Planning: open tax planning and investment declaration for an employee.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Show Tax Planning action",
          edit: "Open / edit tax planning from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Tax Planning)",
          edit: "(Tax Planning → Process / Open)",
        },
      },
      {
        key: "pay_act_loan",
        label: "Loan",
        description:
          "Actions → Loan: apply, sanction, EMI schedule and recovery for an employee.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "Show Loan in Actions hub",
          create: "Submit loan application",
          edit: "Adjust or reschedule loan",
          approve: "Approve or sanction loan",
        },
        actionWhere: {
          view: "(Payroll → Actions → Loan)",
          create: "(Loan → Apply)",
          edit: "(Loan → Adjust)",
          approve: "(Loan → Approve)",
        },
      },
      {
        key: "pay_act_leave_encashment",
        label: "Leave Encashment",
        description:
          "Actions → Leave Encashment: request or process leave encashment for an employee.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "Show Leave Encashment in Actions hub",
          create: "Request encashment",
          edit: "Adjust or reverse encashment",
          approve: "Approve encashment",
        },
        actionWhere: {
          view: "(Payroll → Actions → Leave Encashment)",
          create: "(Leave Encashment → Request)",
          edit: "(Leave Encashment → Reverse)",
          approve: "(Leave Encashment → Approve)",
        },
      },
      {
        key: "pay_act_advance",
        label: "Advance",
        description:
          "Run Payroll → Actions → Advance: create or manage salary advances for an employee.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Show Advance action",
          create: "Create advance from Actions",
          edit: "Manage advance from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Advance)",
          create: "(Advance → Process / Advance)",
          edit: "(Advance modal)",
        },
      },
      {
        key: "pay_act_reimbursement",
        label: "Reimbursement",
        description:
          "Run Payroll → Actions → Reimbursement: open the Reimbursement Control Center (Process / Claims list) for an employee or company-wide.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "Show Reimbursement action",
          create: "Open Control Center / raise on behalf from Actions",
          edit: "Open Control Center processing from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Reimbursement)",
          create: "(Reimbursement → Process / Claims list)",
          edit: "(Reimbursement Control Center)",
        },
      },
      {
        key: "pay_act_reimb_optins",
        label: "Reimbursement Opt-ins",
        description:
          "Control Center → Opt-ins: company-wide list; opt-in on behalf, change amount, or revoke with mandatory remarks (audited HR override).",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Opt-ins list",
          create: "Opt-in on behalf of an employee",
          edit: "Change / revoke opt-in with remarks (HR override)",
        },
        actionWhere: {
          view: "(Payroll → Actions → Reimbursement → Opt-ins)",
          create: "(Opt-ins → Opt-in on behalf)",
          edit: "(Opt-ins → Change / Revoke)",
        },
      },
      {
        key: "pay_act_reimb_claims",
        label: "Reimbursement Claims",
        description:
          "Control Center → Claims: approve, partial, reject, or query flexi reimbursement claims.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "View claims queue / metrics",
          approve: "Approve / partial / reject / query claims",
        },
        actionWhere: {
          view: "(Payroll → Actions → Reimbursement → Claims)",
          approve: "(Claims row → Approve / Partial / Reject / Query)",
        },
      },
      {
        key: "pay_act_reimb_ledger",
        label: "Reimbursement Ledger",
        description:
          "Control Center → Ledger: flexi posting / ledger history for the financial year.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View reimbursement ledger",
          export: "Export ledger CSV",
        },
        actionWhere: {
          view: "(Payroll → Actions → Reimbursement → Ledger)",
          export: "(Ledger → Export)",
        },
      },
      {
        key: "pay_act_reimb_year_end",
        label: "Reimbursement Year-end",
        description:
          "Control Center → Year-end: March / FY-end settle (lapse unclaimed balances per policy).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View year-end settle panel",
          edit: "Run March / FY-end settle",
        },
        actionWhere: {
          view: "(Payroll → Actions → Reimbursement → Year-end)",
          edit: "(Year-end → March / FY-end settle)",
        },
      },
      {
        key: "pay_act_process_fnf",
        label: "Process F&F",
        description:
          "Run Payroll → Actions → Process F&F: Full & Final for relieved employees — unpaid months, settlement heads, F&F List.",
        actions: ["view", "edit", "approve"],
        actionLabels: {
          view: "Show Process F&F action",
          edit: "Process F&F settlement from Actions",
          approve: "Approve / finalise F&F from Actions",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll → Actions → Process F&F)",
          edit: "(Process F&F workspace)",
          approve: "(Process F&F - Approve / Finalise)",
        },
      },
      {
        key: "assigned_structure",
        label: "Assigned Structure",
        description:
          "Older structure export/import/history grants. Prefer Actions → Assigned Structure.",
        actions: ["view", "edit", "export", "create", "delete"],
        actionLabels: {
          view: "View assigned structure",
          edit: "Salary revision and statutory options",
          create: "Bulk import salary revisions",
          export: "Export assigned structure Excel",
          delete: "Delete latest salary or structure revision",
        },
        actionWhere: {
          view: "(Assigned Structure / Profile CTC)",
          edit: "(Employee profile · CTC)",
          create: "(Payroll → Import → Salary Revision)",
          export: "(Assigned Structure · Export Excel)",
          delete: "(Salary Revision history · Delete)",
        },
      },
      {
        key: "structure_value_override",
        label: "Override Structure Values",
        description:
          "Override a Formula / Balance CTC component amount on one employee's Auto salary structure (reason mandatory, audited). Special Allowance absorbs the difference; Reset to formula restores the rule.",
        actions: ["edit"],
        actionLabels: {
          edit: "Override / Reset formula component amounts; Keep overrides on Salary Revision",
        },
        actionWhere: {
          edit: "(Employee profile - CTC eye - Salary Structure - click a formula amount; hover shows how) or (Payroll → Run Payroll → Actions - Salary Revision - Overridden components: Keep)",
        },
      },
      {
        key: "salary_revision_import",
        label: "Salary Revision Import",
        description:
          "Bulk import salary revisions from Excel: Amount/%age or Manual components, Increment Effective Date/Month, Pay Month, and same-month Continue.",
        actions: ["view", "create", "import"],
        actionLabels: {
          view: "Open Salary Revision Import",
          create: "Import salary revisions from Excel",
          import: "Upload Salary Revision Excel",
        },
        actionWhere: {
          view: "(Payroll → Import → Salary Revision)",
          create: "(Salary Revision Import modal - Import; Excel Pay Month + Continue Same Month Increment)",
          import: "(Salary Revision Import modal - Choose Excel file / Download template)",
        },
      },
      {
        key: "salary_structure_component_import",
        label: "Structure Component Import",
        description:
          "Bulk import Manual fixed component amounts for employees under a salary structure. Profile amount (CTC or Gross per structure mode) remains the source of truth; Formula and Balance CTC stay derived.",
        actions: ["view", "create", "import"],
        actionLabels: {
          view: "Open Structure Component Import",
          create: "Import Manual component values from Excel",
          import: "Upload Structure Components Excel",
        },
        actionWhere: {
          view:
            "(Import / Export → Payroll → Import Structure Components; also Payroll → Import → Structure Components)",
          create: "(Structure Components Import modal - Import)",
          import: "(Structure Components Import modal - Upload Excel)",
        },
      },
      {
        key: "update_statutory_applicability",
        parentKey: "payroll_settings",
        label: "Update Statutory & Benefits Applicability",
        description:
          "Effective-dated PF / Ceiling PF / ESI / PT / Gratuity and PB / NPS / VPF / Meal applicability per employee (also from Profile Salary Structure).",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Statutory & Benefits Applicability History",
          edit: "Save Statutory & Benefits Applicability Change",
        },
        actionWhere: {
          view: "(Assigned Structure - Update Statutory & Benefits Applicability modal) or (Profile Salary Structure - Statutory)",
          edit: "(modal - Save) or (Profile Salary Structure - Set effective month)",
        },
      },
      {
        key: "loan",
        label: "Loan",
        description: "Older loan grants. Prefer Actions → Loan.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "Open loan",
          create: "Submit loan application",
          edit: "Adjust or reschedule loan",
          approve: "Approve or sanction loan",
        },
        actionWhere: {
          view: "(Loan)",
          create: "(Loan · Apply)",
          edit: "(Loan · Adjust)",
          approve: "(Loan · Approve)",
        },
      },
      {
        key: "leave_encashment",
        label: "Leave Encashment",
        description: "Older leave encashment grants. Prefer Actions → Leave Encashment.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "Open leave encashment",
          create: "Request or bulk encash",
          edit: "Adjust or reverse encashment",
          approve: "Approve encashment",
        },
        actionWhere: {
          view: "(Leave Encashment)",
          create: "(Leave Encashment · Request)",
          edit: "(Leave Encashment · Reverse)",
          approve: "(Leave Encashment · Approve)",
        },
      },
      {
        key: "run_payroll",
        label: "Open Run Payroll wizard",
        description:
          "Opens the Run Payroll wizard. Auto-enabled when any Run Payroll step below is granted; keep View on so the menu appears.",
        actions: ["view"],
        actionLabels: {
          view: "Open Run Payroll wizard",
        },
        actionWhere: {
          view: "(Payroll → Run Payroll)",
        },
      },
      {
        key: "payroll_checklist",
        label: "Checklist Tasks",
        description:
          "Company-configurable payroll checklist items with frequency rules (monthly, quarterly, one-time, date range).",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Checklist Tasks",
          create: "Create Checklist",
          edit: "Edit Checklist / Mark Complete",
          delete: "Delete Checklist",
        },
        actionWhere: {
          view: "(Payroll → Checklist Tasks) or (Run Payroll - Checklist step)",
          create: "(Checklist Tasks - Add)",
          edit: "(Checklist Tasks / completion checkboxes)",
          delete: "(Checklist Tasks - Delete)",
        },
      },
      {
        key: "salary_structure",
        parentKey: "payroll_settings",
        label: "Salary Structure",
        description: "Defines salary components and salary structure templates.",
        actions: ["view", "create", "edit", "delete", "export"],
        actionLabels: {
          view: "View Salary Structures",
          create: "Create New Structure",
          edit: "Edit Structure (incl. NPS / VPF / Meal Voucher / PB)",
          delete: "Delete Structure",
          export: "Export Breakdown (Excel / PDF)",
        },
        actionWhere: {
          view: "(structure list) or (employee profile - CTC field eye icon)",
          create: "(New Structure button)",
          edit:
            "(row menu) or (salary structure modal - Save) or (structure form - Enable NPS/VPF/Meal/PB + Configure details)",
          delete: "(row menu)",
          export: "(employee salary structure modal - Export Excel / Export PDF)",
        },
      },
      {
        key: "statutory_rates",
        parentKey: "payroll_settings",
        label: "Statutory Rates",
        description: "Maintains statutory deduction and compliance rates.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Statutory Rates",
          edit: "Update Rates",
        },
        actionWhere: {
          view: "(rates table)",
          edit: "(inline or row edit)",
        },
      },
      {
        key: "tds_reference_heads",
        parentKey: "statutory_rates",
        label: "TDS Reference Heads",
        description:
          "System master of income-tax classification heads mapped to salary components for TDS / Form-16.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View TDS Reference Heads",
          edit: "Create / Edit Heads (Super Admin) · Configure tax rule versions",
        },
        actionWhere: {
          view: "(Payroll → Settings → Statutory rates / TDS Reference Heads)",
          edit: "(TDS Reference Heads - Add / Pencil / Configure gear; Super Admin)",
        },
      },
      {
        key: "flexi_benefits",
        parentKey: "payroll_settings",
        label: "Reimbursement Settings",
        description:
          "Payroll → Settings → Reimbursements: enable Flexi, payment mode, opt-in / opt-out policy, and related company settings. Control Center tabs are separate Actions screens.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View Reimbursement settings / balances / payslip reimbursement tab",
          create: "Create Flexi declaration or claim (legacy ESS path)",
          edit: "Edit Reimbursement settings / Flexi component Method A/B config",
          approve: "Legacy approve flag (prefer Reimbursement Claims Action)",
        },
        actionWhere: {
          view: "(Payroll → Settings → Reimbursements) or (Run Payroll → Payslips → Reimbursement)",
          create: "(legacy ESS / Employee Payroll Reimbursement)",
          edit: "(Payroll → Settings → Reimbursements) or (Salary Structure → component → Flexi Benefit)",
          approve: "(prefer Actions → Reimbursement Claims)",
        },
      },
      {
        key: "formula_manager",
        parentKey: "payroll_settings",
        label: "Formula Manager",
        description: "Creates and edits payroll calculation formulas.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Formulas",
          create: "Create Formula",
          edit: "Edit Formula",
          delete: "Delete Formula",
        },
        actionWhere: {
          view: "(formula list)",
          create: "(New Formula button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "process_payroll",
        label: "Process Payroll",
        description: "Runs payroll processing and approval for a pay cycle.",

        actions: ["view", "create", "approve", "export"],
        actionLabels: {
          view: "View Payroll Processing",
          create: "Process Payroll",
          approve: "Approve Payroll",
          export: "Export Quick Reports (Projection / Register / Reconciliation / Journal)",
        },
        actionWhere: {
          view: "(processing page) or (Payroll → Run Payroll → Process)",
          create: "(Process Payroll button)",
          approve: "(Approve button)",
          export: "(Process step - Quick Reports dropdown above grid)",
        },
      },
      {
        key: "pay_proc_new_joinees",
        label: "New Joinees",
        description: "Validate new joinee readiness before process.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open New Joinees step", edit: "Validate / update New Joinees" },
        actionWhere: { view: "(Run Payroll → New Joinees)", edit: "(New Joinees → Validate)" },
      },
      {
        key: "pay_proc_left_employees",
        label: "Left Employees",
        description: "Review left employees for the pay month.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Left Employees step", edit: "Validate Left Employees" },
        actionWhere: { view: "(Run Payroll → Left Employees)", edit: "(Left Employees → Validate)" },
      },
      {
        key: "pay_proc_salary_revisions",
        label: "Salary Revisions",
        description: "Review salary revisions for the pay month.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Salary Revisions step", edit: "Validate Salary Revisions" },
        actionWhere: { view: "(Run Payroll → Salary Revisions)", edit: "(Salary Revisions → Validate)" },
      },
      {
        key: "pay_proc_attendance_validation",
        label: "Attendance",
        description: "Confirm attendance lock and status summary before process.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Attendance step", edit: "Validate Attendance gate" },
        actionWhere: { view: "(Run Payroll → Attendance)", edit: "(Attendance → Validate)" },
      },
      {
        key: "pay_proc_lop_reversal_arrears",
        label: "LOP Reversal & Arrears",
        description: "Review LOP reversals and arrears for the pay month.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open LOP Reversal step", edit: "Validate LOP Reversal & Arrears" },
        actionWhere: { view: "(Run Payroll → LOP Reversal)", edit: "(LOP Reversal → Validate)" },
      },
      {
        key: "pay_proc_one_time_inputs",
        label: "Monthly Variables",
        description: "Enter or import monthly variable components.",
        actions: ["view", "edit", "import"],
        actionLabels: {
          view: "Open Monthly Variables step",
          edit: "Add / edit monthly variables",
          import: "Bulk import monthly variables",
        },
        actionWhere: {
          view: "(Run Payroll → Monthly Variables)",
          edit: "(Monthly Variables → Add / Edit)",
          import: "(Monthly Variables → Import)",
        },
      },
      {
        key: "pay_proc_bank_details",
        label: "Bank Details",
        description: "Validate bank account readiness for disbursement.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Bank Details step", edit: "Validate Bank Details" },
        actionWhere: { view: "(Run Payroll → Bank Details)", edit: "(Bank Details → Validate)" },
      },
      {
        key: "pay_proc_headcount_reconcile",
        label: "Headcount Reconciliation",
        description: "Reconcile opening, joiners, leavers and closing headcount.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Headcount step", edit: "Validate Headcount Reconciliation" },
        actionWhere: { view: "(Run Payroll → Headcount)", edit: "(Headcount → Validate)" },
      },
      {
        key: "pay_proc_process",
        label: "Process Payroll",
        description:
          "View opens the step. Edit shows Process / Reprocess / Unprocess. Quick reports are nested grants under this step.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Process Payroll step",
          edit: "Show Process / Reprocess / Unprocess buttons",
        },
        actionWhere: {
          view: "Run Payroll → Process",
          edit: "Process step footer · Process / Reprocess / Unprocess",
        },
      },
      {
        key: "pay_proc_checklist",
        label: "Checklist",
        description: "Complete payroll checklist tasks for the month.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Checklist step", edit: "Mark checklist items complete" },
        actionWhere: { view: "(Run Payroll → Checklist)", edit: "(Checklist step)" },
      },
      {
        key: "pay_proc_disbursement",
        label: "Disbursement",
        description: "Mark paid / hold amounts for the pay month.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Disbursement step", edit: "Update paid / hold" },
        actionWhere: { view: "(Run Payroll → Disbursement)", edit: "(Disbursement)" },
      },
      {
        key: "pay_proc_preview_validate",
        label: "Preview & Validate",
        description: "Review all step summaries before finalise.",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Preview & Validate step", edit: "Validate preview" },
        actionWhere: { view: "(Run Payroll → Preview & Validate)", edit: "(Preview → Validate)" },
      },
      {
        key: "pay_proc_finalise",
        label: "Finalise Payroll",
        description: "Lock the company payroll month.",
        actions: ["view", "approve"],
        actionLabels: { view: "Open Finalise step", approve: "Finalise / unlock payroll month" },
        actionWhere: { view: "(Run Payroll → Finalise)", approve: "(Finalise → Validate / Unlock)" },
      },
      {
        key: "pay_proc_payslips",
        label: "Payslips",
        description:
          "Process → Payslips step: view listing, download, customise templates, publish to ESS.",
        actions: ["view", "edit", "export", "approve"],
        actionLabels: {
          view: "Open Payslips step",
          edit: "Customise template tags",
          export: "ZIP or email payslips",
          approve: "Publish or unpublish to ESS",
        },
        actionWhere: {
          view: "Payroll → Process → Payslips",
          edit: "Payslips · Customise",
          export: "Payslips · ZIP / Email",
          approve: "Payslips · Publish",
        },
      },
      {
        key: "payslips",
        label: "Payslips",
        description:
          "Reports → Payslips: view and download. For download-only grant View + Export here. Leave Approve and Edit off. Do not grant other Process steps.",
        actions: ["view", "export", "approve", "edit"],
        actionLabels: {
          view: "Open payslip listing and templates",
          edit: "Customise template tags",
          export: "ZIP download or email payslips",
          approve: "Publish or unpublish to ESS",
        },
        actionWhere: {
          view: "Payroll → Reports → Payslips",
          edit: "Payslips · Customise",
          export: "Payslips · ZIP / Email",
          approve: "Payslips · Publish",
        },
      },
      {
        key: "pay_rpt_projection",
        parentKey: "pay_proc_process",
        label: "Payroll Projection",
        description: "Process → Quick Reports → Payroll Projection Excel.",
        actions: ["view", "export"],
        actionLabels: { view: "See Payroll Projection in Quick Reports", export: "Download Payroll Projection" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Payroll Projection)" },
      },
      {
        key: "pay_rpt_register",
        parentKey: "pay_proc_process",
        label: "Payroll Register",
        description: "Process → Quick Reports → Payroll Register Excel.",
        actions: ["view", "export"],
        actionLabels: { view: "See Payroll Register in Quick Reports", export: "Download Payroll Register" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Payroll Register)" },
      },
      {
        key: "pay_rpt_reconciliation",
        parentKey: "pay_proc_process",
        label: "Payroll Reconciliation",
        description: "Process → Quick Reports → Payroll Reconciliation Excel.",
        actions: ["view", "export"],
        actionLabels: { view: "See Payroll Reconciliation in Quick Reports", export: "Download reconciliation" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Payroll Reconciliation)" },
      },
      {
        key: "pay_rpt_journal",
        parentKey: "pay_proc_process",
        label: "Accounting Journal",
        description: "Process → Quick Reports → Accounting Journal Excel.",
        actions: ["view", "export"],
        actionLabels: { view: "See Accounting Journal in Quick Reports", export: "Download journal" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Accounting Journal)" },
      },
      {
        key: "pay_rpt_unpaid",
        parentKey: "pay_proc_process",
        label: "Unpaid Salary Report",
        description: "Process → Quick Reports → Unpaid Salary.",
        actions: ["view", "export"],
        actionLabels: { view: "See Unpaid Salary in Quick Reports", export: "Download unpaid salary" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Unpaid Salary)" },
      },
      {
        key: "pay_rpt_leave_encashment_away",
        parentKey: "pay_proc_process",
        label: "Leave Encashment Away",
        description: "Process → Quick Reports → Leave Encashment Away.",
        actions: ["view", "export"],
        actionLabels: { view: "See Leave Encashment Away in Quick Reports", export: "Download report" },
        actionWhere: { view: "(Process → Quick Reports)", export: "(Quick Reports → Leave Encashment Away)" },
      },
      {
        key: "salary_revision_report",
        label: "Salary Revision Report",
        description:
          "Month-wise salary revision report with filters and Excel download under Payroll Reports.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Salary Revision Report",
          export: "Download Salary Revision Report",
        },
        actionWhere: {
          view: "(Payroll → Reports → Salary Revision Report)",
          export: "(Salary Revision Report — Download)",
        },
      },
      {
        key: "unpaid_salary_report",
        label: "Unpaid Salary",
        description:
          "Open unpaid salary and F&F obligations with month-wise and cumulative accumulation Excel export.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Unpaid Salary report",
          export: "Download Unpaid Salary Excel",
        },
        actionWhere: {
          view: "(Payroll → Reports → Unpaid Salary)",
          export: "(Unpaid Salary — Export Excel)",
        },
      },
      {
        key: "form24q_filing",
        label: "e-TDS Return",
        description:
          "Quarterly e-TDS return (Form 24Q): period, review, challans/mapping, RPU/FVU, record PRN, and corrections.",
        actions: ["view", "create", "edit", "export", "approve"],
        actionLabels: {
          view: "Open e-TDS Return wizard",
          create: "Prepare return / add challans / start correction",
          edit: "Edit challans, mappings, CSI import, correction types",
          export: "Download RPU ZIP / Form 138 Excel / run FVU / download artifacts",
          approve: "Record filing (PRN) and lock quarter",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → e-TDS Return)",
          create: "(Form 24Q Step 1 — Port Data / Prepare return / Start correction)",
          edit: "(Form 24Q — Step 3 Challans & Link)",
          export: "(Form 24Q — RPU & FVU step — Download RPU ZIP / Form 138 Excel / Run FVU / Artifacts)",
          approve: "(Form 24Q — Record filing)",
        },
      },
      {
        key: "tds_engine_compliance",
        parentKey: "statutory_rates",
        label: "TDS Engine / Compliance Updates",
        description:
          "Install salary TDS FVU packs (Form 24Q up to FY 2025-26, Form 138 from TY 2026-27) via zip + bundled RPU.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "Open Compliance Updates tab",
          edit: "Install / update salary FVU packs and fresh-start catalog",
        },
        actionWhere: {
          view: "(Payroll → Settings → Compliance Updates)",
          edit: "(Compliance Updates — Install / Update cards / Fresh start)",
        },
      },
      {
        key: "statutory_filing",
        label: "Statutory Filing",
        description:
          "PF, ESI, PT, NPS, Meal voucher remittance statements and Form 24Q quarterly TDS filing under Payroll.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Statutory Filing section",
          export: "Export statutory contribution Excel",
        },
        actionWhere: {
          view: "(Payroll left nav — Statutory Filing)",
          export: "(Statutory Filing — Export Excel)",
        },
      },
      {
        key: "statutory_filing_pf",
        label: "PF Statements",
        description:
          "Monthly Provident Fund contribution register from processed payroll (Excel export).",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open PF under Statutory Filing",
          export: "Export PF contribution Excel",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → PF)",
          export: "(PF Statements — Export Excel)",
        },
      },
      {
        key: "statutory_filing_esi",
        label: "ESI Statements",
        description:
          "Monthly ESI contribution register from processed payroll (Excel export).",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open ESI under Statutory Filing",
          export: "Export ESI contribution Excel",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → ESI)",
          export: "(ESI Statements — Export Excel)",
        },
      },
      {
        key: "statutory_filing_pt",
        label: "PT",
        description:
          "Monthly Professional Tax contribution register from processed payroll (Excel export).",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open PT under Statutory Filing",
          export: "Export PT contribution Excel",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → PT)",
          export: "(PT — Export Excel)",
        },
      },
      {
        key: "statutory_filing_nps",
        label: "NPS Remittance",
        description:
          "Employer NPS amounts from processed payroll with PRAN for CRA / corporate NPS remittance export.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open NPS under Statutory Filing",
          export: "Export NPS remittance Excel",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → NPS)",
          export: "(NPS Remittance — Export Excel)",
        },
      },
      {
        key: "statutory_filing_meal",
        label: "Meal Voucher Disbursement",
        description:
          "Meal voucher Fixed CTC amounts from processed payroll for vendor / finance disbursement export.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Meal Voucher under Statutory Filing",
          export: "Export Meal voucher Excel",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → Meal Voucher)",
          export: "(Meal Voucher — Export Excel)",
        },
      },
      {
        key: "tds_challans",
        label: "TDS Challans",
        description:
          "Challan register, balances, and Link Deductions — managed inside e-TDS Return Step 3 (Challans & Link).",
        actions: ["view", "create", "edit", "delete", "approve"],
        actionLabels: {
          view: "View challan register in e-TDS Return Step 3",
          create: "Add challan",
          edit: "Edit challan / Link deductions",
          delete: "Delete challan",
          approve: "Reverse utilization",
        },
        actionWhere: {
          view: "(Payroll → Statutory Filing → e-TDS Return → Step 3)",
          create: "(e-TDS Return Step 3 — Add challan)",
          edit: "(e-TDS Return Step 3 — Link / Edit)",
          delete: "(e-TDS Return Step 3 — Delete)",
          approve: "(e-TDS Return Step 3 — Reverse consumption)",
        },
      },
      {
        key: "tax_declaration",
        label: "Tax Planning",
        description: "Manages employee tax planning declarations and approvals.",
        actions: ["view", "create", "edit", "approve", "export"],
        actionLabels: {
          view: "View Tax Planning",
          create: "Upload Declaration",
          edit: "Update Declaration",
          approve: "Approve Declaration",
          export: "Export Declaration",
        },
        actionWhere: {
          view:
            "(declaration list; Tax Planning modal) or (Run Payroll → Declarations and TDS → Declarations / TDS Comparison — Tax Planning)",
          create: "(Upload button)",
          edit: "(row menu - Edit)",
          approve: "(Approve button)",
          export: "(Export button)",
        },
      },
      {
        key: "payroll_settings",
        label: "General Settings",
        description: "Payroll → Settings → General Settings, Declaration, and Payroll Setup. Nested rows grant Salary Structures, Statutory, Reimbursements, Policies, and related tools.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Payroll Settings",
          edit: "Save Payroll Settings",
        },
        actionWhere: {
          view: "(Payroll → Settings)",
          edit:
            "(Payroll Settings - Save) or (General - Finalise/Unfinalise role notifications)",
        },
      },
      {
        key: "pay_set_policies",
        parentKey: "payroll_settings",
        label: "Policies",
        description: "Payroll → Settings → Policies hub (loan and leave encashment).",
        actions: ["view", "edit"],
        actionLabels: { view: "Open Policies settings", edit: "Edit any payroll policy" },
        actionWhere: { view: "Payroll → Settings → Policies", edit: "Save policy" },
      },
      {
        key: "loan_policies",
        parentKey: "pay_set_policies",
        label: "Loan Policies",
        description: "Configurable employer loan types, limits, interest, recovery, payroll mapping and perquisite.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Loan Policies",
          create: "Create Loan Policy",
          edit: "Save Loan Policy",
          delete: "Delete Loan Policy",
        },
        actionWhere: {
          view: "(Payroll → Settings → Policies → Loan policies)",
          create: "(Policies - Add policy)",
          edit: "(Policies - Save policy)",
          delete: "(Policies - Delete)",
        },
      },
      {
        key: "leave_encashment_policies",
        parentKey: "pay_set_policies",
        label: "Leave Encashment Policies",
        description: "Encashment eligibility, day limits, frequency, salary basis, tax split and payroll mapping.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Leave Encashment Policies",
          create: "Create Encashment Policy",
          edit: "Save Encashment Policy",
          delete: "Delete Encashment Policy",
        },
        actionWhere: {
          view: "(Payroll → Settings → Policies → Leave encashment policies)",
          create: "(Policies - Add policy)",
          edit: "(Policies - Save policy)",
          delete: "(Policies - Delete)",
        },
      },
      {
        key: "pay_action_hub",
        label: "Action Hub",
        description: "Open Action Hub filtered to payroll request monitoring.",
        actions: ["view", "approve"],
        actionLabels: {
          view: "Open Payroll Action Hub",
          approve: "Approve / Reject payroll desk items",
        },
        actionWhere: { view: "Action Hub · Payroll", approve: "Action Hub detail" },
      },
    ],
  },
  {
    key: "hiring",
    label: "Hiring",
    path: "/admin/hiring",
    description:
      "Job request to open job, candidates, ATS (including offers), and handoff to employee onboarding.",
    actions: ["view", "create", "edit", "delete", "approve"],
    screens: [
      {
        key: "dashboard",
        label: "Dashboard",
        description: "Hiring pipeline snapshot.",
        actions: ["view"],
        actionLabels: { view: "View Hiring Dashboard" },
        actionWhere: { view: "(Admin nav → Hiring → Dashboard)" },
      },
      {
        key: "mrf_jobs",
        label: "Jobs",
        description:
          "Raise job requests, approve via workflow, open jobs; admin may add a job directly.",
        actions: ["view", "create", "edit", "delete", "approve"],
        actionLabels: {
          view: "View Jobs",
          create: "Raise job request / Add job",
          edit: "Edit job",
          delete: "Cancel / Close job",
          approve: "Approve job request",
        },
        actionWhere: {
          view: "(Admin nav → Hiring → Jobs)",
          create: "(Jobs → Add job)",
          edit: "(Jobs → row menu)",
          delete: "(Jobs → row menu)",
          approve: "(Jobs → Pending approval → Approve — hiring policy workflow)",
        },
      },
      {
        key: "candidates",
        label: "Candidates",
        description: "Candidate bank, invite profile link, and profile status.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Candidates",
          create: "Add Candidate",
          edit: "Update Candidate",
          delete: "Remove Candidate",
        },
        actionWhere: {
          view: "(Admin nav → Hiring → Candidates)",
          create: "(Add Candidate button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "ats",
        label: "ATS",
        description:
          "Submit candidates to jobs, move stages, and manage offers within the pipeline.",
        actions: ["view", "create", "edit", "approve"],
        actionLabels: {
          view: "View ATS",
          create: "Submit to Job",
          edit: "Update Stage / Offer",
          approve: "Advance Stage / Offer",
        },
        actionWhere: {
          view: "(Admin nav → Hiring → ATS)",
          create: "(Submit button)",
          edit: "(stage / offer action)",
          approve: "(stage / offer action)",
        },
      },
      {
        key: "referrals",
        label: "Referrals",
        description: "Candidate referrals tracked in hiring.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Referrals",
          create: "Add Referral",
          edit: "Update Referral",
        },
        actionWhere: {
          view: "(Admin nav → Hiring → Referrals)",
          create: "(Add referral)",
          edit: "(row menu)",
        },
      },
      {
        key: "hiring_settings",
        label: "Hiring Settings",
        description:
          "Hiring policies, ID formats, mandatory profile fields, and recruitment portal email/settings.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Hiring Settings",
          edit: "Edit Hiring Settings",
        },
        actionWhere: {
          view: "(Global Settings → Hiring)",
          edit: "(Global Settings → Hiring → Save)",
        },
      },
    ],
  },
  {
    key: "assets",
    label: "Assets",
    path: "/admin/assets",
    description: "Company asset inventory and assignments.",
    actions: ["view", "create", "edit", "delete", "export", "approve"],
    screens: [
      {
        key: "asset_dashboard",
        label: "Asset Dashboard",
        description: "KPI overview of stock, assignments, and pending work.",
        actions: ["view"],
        actionLabels: { view: "View Asset Dashboard" },
        actionWhere: { view: "(Assets → Dashboard)" },
      },
      {
        key: "asset_list",
        label: "Asset Register",
        description: "Maintains asset inventory and lifecycle records.",
        actions: ["view", "create", "edit", "delete", "export"],
        actionLabels: {
          view: "View Asset Register",
          create: "Add New Asset",
          edit: "Edit Asset Details",
          delete: "Delete Asset",
          export: "Export Asset List",
        },
        actionWhere: {
          view: "(Assets → Asset Register)",
          create: "(Add Asset button)",
          edit: "(row menu)",
          delete: "(row menu)",
          export: "(toolbar - Export)",
        },
      },
      {
        key: "assign_asset",
        label: "Assign Asset",
        description: "Assigns and updates assets mapped to employees.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Asset Assignments",
          create: "Assign Asset to Employee",
          edit: "Update Assignment",
        },
        actionWhere: {
          view: "(Assets → Operations / Register assign)",
          create: "(Assign button)",
          edit: "(row menu)",
        },
      },
      {
        key: "asset_requests",
        label: "Asset Requests",
        description: "Admin review of employee asset requests.",
        actions: ["view", "edit", "approve"],
        actionLabels: {
          view: "View Asset Requests",
          edit: "Reserve / Assign Requests",
          approve: "Approve or Reject Requests",
        },
        actionWhere: {
          view: "(Assets → Requests)",
          edit: "(request row actions)",
          approve: "(Approve / Reject buttons)",
        },
      },
      {
        key: "asset_operations",
        label: "Asset Operations",
        description: "Stock, transfer, return, maintenance, scrap, and recovery.",
        actions: ["view", "create", "edit"],
        actionLabels: {
          view: "View Operations",
          create: "Create Operation Actions",
          edit: "Complete Operation Actions",
        },
        actionWhere: {
          view: "(Assets → Operations)",
          create: "(operation actions)",
          edit: "(operation actions)",
        },
      },
      {
        key: "asset_settings",
        label: "Asset Settings",
        description: "Categories, types, numbering, and workflow rules.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Asset Settings",
          edit: "Edit Asset Settings",
        },
        actionWhere: {
          view: "(Settings hub → Assets → Settings)",
          edit: "(Save / CRUD masters)",
        },
      },
      {
        key: "asset_import",
        label: "Asset Import / Export",
        description: "Bulk import templates and exports for asset datasets.",
        actions: ["view", "create", "export"],
        actionLabels: {
          view: "View Import / Export",
          create: "Upload Import File",
          export: "Download Export / Template",
        },
        actionWhere: {
          view: "(Import/Export hub → Assets)",
          create: "(Upload file)",
          export: "(Export / Template)",
        },
      },
      {
        key: "asset_reports",
        label: "Asset Reports",
        description: "Shows downloadable reports for asset movements.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Asset Reports",
          export: "Download Report",
        },
        actionWhere: {
          view: "(Reports hub → Assets)",
          export: "(Export button)",
        },
      },
    ],
  },
  {
    key: "communication",
    label: "Communication",
    path: "/admin/communication",
    description: "Flash news, announcements, polls, company policies.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "flash_news",
        label: "Flash News",
        description: "Publishes short internal flash updates to users.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Flash News",
          create: "Create Flash News",
          edit: "Edit Flash News",
          delete: "Delete Flash News",
        },
        actionWhere: {
          view: "(flash news page)",
          create: "(New News button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "announcements",
        label: "Announcements",
        description: "Publishes organization-wide formal announcements.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Announcements",
          create: "Create Announcement",
          edit: "Edit Announcement",
          delete: "Delete Announcement",
        },
        actionWhere: {
          view: "(announcements page)",
          create: "(New Announcement button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "polls",
        label: "Polls",
        description: "Creates and manages employee polls and votes.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Polls",
          create: "Create Poll",
          edit: "Edit / Publish Poll",
          delete: "Delete Poll",
        },
        actionWhere: {
          view: "(polls page)",
          create: "(New Poll button)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "company_policy",
        label: "Company Policy",
        description: "Publishes company policy documents to employees.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Company Policies",
          create: "Add Company Policy",
          edit: "Edit Company Policy",
          delete: "Delete Company Policy",
        },
        actionWhere: {
          view: "(Communication → Company Policy)",
          create: "(Add Policy button)",
          edit: "(row Edit)",
          delete: "(row Delete)",
        },
      },
    ],
  },
  {
    key: "helpdesk",
    label: "Helpdesk",
    path: "/admin/helpdesk",
    description: "Ticket inbox and helpdesk configuration.",
    actions: ["view", "create", "edit", "delete", "approve"],
    screens: [
      {
        key: "tickets",
        label: "Tickets",
        description: "Manages support tickets and resolution workflow.",
        actions: ["view", "create", "edit", "delete", "approve"],
        actionLabels: {
          view: "View Tickets",
          create: "Create Ticket",
          edit: "Update Ticket Status",
          delete: "Delete Ticket",
          approve: "Approve Resolution",
        },
        actionWhere: {
          view: "(tickets list)",
          create: "(New Ticket button)",
          edit: "(ticket detail - Save)",
          delete: "(row menu)",
          approve: "(Approve button)",
        },
      },
      {
        key: "helpdesk_config",
        label: "Helpdesk Config",
        description: "Configures helpdesk ticket types, categories, and company defaults.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Configuration",
          edit: "Edit Configuration",
        },
        actionWhere: {
          view: "(Helpdesk - Catalog)",
          edit: "(Catalog forms / category Configure)",
        },
      },
      {
        key: "helpdesk_level_policies",
        label: "Level Policies",
        description: "Category-level priorities, SLA, workflow, RCA, and assignment overrides.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Level Overrides",
          edit: "Edit Level Overrides",
        },
        actionWhere: {
          view: "(Catalog - Category Configure / Default priorities)",
          edit: "(Catalog - Configure / Default priorities forms)",
        },
      },
      {
        key: "ticket_actions",
        label: "Ticket Assignment Actions",
        description: "Assign, delegate, and remind on helpdesk tickets.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Assignment Panel",
          edit: "Assign / Delegate / Remind",
        },
        actionWhere: {
          view: "(ticket detail)",
          edit: "(Assign / Delegate / Remind buttons)",
        },
      },
      {
        key: "ticket_feedback",
        label: "Ticket Feedback",
        description: "Customer rating and comments on closed tickets; feedback reports.",
        actions: ["view", "create"],
        actionLabels: {
          view: "View Feedback Reports",
          create: "Submit Feedback Rating",
        },
        actionWhere: {
          view: "(Reports - Closure feedback)",
          create: "(Closed ticket - Submit feedback)",
        },
      },
    ],
  },
  {
    key: "user_roles",
    label: "User Roles",
    path: "/admin/user-role-management",
    description: "Role creation (Action Based workflow roles or Access Based with screen/data) + access configuration.",
    actions: ["view", "create", "edit", "delete"],
    screens: [
      {
        key: "role_list",
        label: "Role List",
        description:
          "Creates and manages roles. Action Based roles finish after create (no Screen/Data). Access Based roles continue into Screen and Data Access.",
        actions: ["view", "create", "edit", "delete"],
        actionLabels: {
          view: "View Roles",
          create: "Create Custom Role (Action or Access Based)",
          edit: "Edit Role Details",
          delete: "Delete Role",
        },
        actionWhere: {
          view: "(roles list)",
          create: "(Add role — Action Based finishes; Access Based opens access wizard)",
          edit: "(row menu)",
          delete: "(row menu)",
        },
      },
      {
        key: "screen_access",
        label: "Screen Access",
        description: "Controls module/screen-level action permissions.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Screen Permissions",
          edit: "Update Screen Permissions",
        },
        actionWhere: {
          view: "(permissions grid)",
          edit: "(Save button)",
        },
      },
      {
        key: "data_access",
        label: "Data Access",
        description: "Configures data visibility scope for each role.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Data Scope",
          edit: "Update Data Scope",
        },
        actionWhere: {
          view: "(scope grid)",
          edit: "(Save button)",
        },
      },
      {
        key: "roles_export",
        label: "Export Roles & Permissions",
        description: "Download roles and screen permissions as Excel from Import / Export.",
        actions: ["view", "export"],
        actionLabels: {
          view: "Open Export Roles",
          export: "Download roles Excel",
        },
        actionWhere: {
          view: "(Import / Export → User role management → Export)",
          export: "(Export Roles & permissions)",
        },
      },
      {
        key: "roles_import",
        label: "Import Roles & Permissions",
        description: "Upload roles and screen permissions Excel from Import / Export.",
        actions: ["view", "import"],
        actionLabels: {
          view: "Open Import Roles",
          import: "Validate and import roles Excel",
        },
        actionWhere: {
          view: "(Import / Export → User role management → Import)",
          import: "(Import Roles & permissions)",
        },
      },
    ],
  },
  {
    key: "statutory_reports",
    label: "Statutory Reports",
    path: "/admin/statutory-reports",
    description:
      "Karnataka Shops & Establishment, Factories Act, and Central PF/ESI statutory registers and returns.",
    actions: ["view", "export"],
    screens: [
      {
        key: "statutory_reports_hub",
        label: "Statutory Reports Hub",
        description: "Generate statutory compliance registers from payroll, attendance, and leave data.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Statutory Reports",
          export: "Export Excel / PDF",
        },
        actionWhere: {
          view: "(Reports workspace → Statutory Reports)",
          export: "(Load report → Excel / PDF buttons)",
        },
        flowGroup: "Reports",
      },
      {
        key: "statutory_form_aliases",
        label: "Statutory Form Names",
        description: "Save custom display names per state, act, and form.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View saved form names",
          edit: "Save custom form name",
        },
        actionWhere: {
          view: "(Statutory Reports → form name field)",
          edit: "(Save form name button)",
        },
        flowGroup: "Configuration",
      },
    ],
  },
  {
    key: "workspaces",
    label: "Workspaces",
    path: "/admin/workspace/reports",
    description:
      "Global hubs for Reports, Import/Export, Checklist, and Settings shared across modules.",
    actions: ["view"],
    screens: [
      {
        key: "reports_workspace",
        label: "Reports Workspace",
        description: "Open the global Reports hub from the Admin sidebar.",
        actions: ["view"],
        actionLabels: { view: "View Reports Workspace" },
        actionWhere: { view: "(Admin sidebar → Reports)" },
        flowGroup: "Hubs",
      },
      {
        key: "import_export_workspace",
        label: "Import / Export Workspace",
        description: "Open the global Import / Export hub.",
        actions: ["view"],
        actionLabels: { view: "View Import / Export Workspace" },
        actionWhere: { view: "(Admin sidebar → Import / Export)" },
        flowGroup: "Hubs",
      },
      {
        key: "checklist_workspace",
        label: "Checklist Workspace",
        description: "Open the global Checklist hub.",
        actions: ["view"],
        actionLabels: { view: "View Checklist Workspace" },
        actionWhere: { view: "(Admin sidebar → Checklist)" },
        flowGroup: "Hubs",
      },
      {
        key: "settings_workspace",
        label: "Settings Workspace",
        description: "Open the global Settings hub.",
        actions: ["view"],
        actionLabels: { view: "View Settings Workspace" },
        actionWhere: { view: "(Admin sidebar → Settings)" },
        flowGroup: "Hubs",
      },
      {
        key: "activity_log_monitor",
        label: "Activity Log Monitor",
        description:
          "Company audit trail of product activity with hot retention and archives.",
        actions: ["view", "export"],
        actionLabels: {
          view: "View Activity Log Monitor",
          export: "Export activity log Excel",
        },
        actionWhere: {
          view: "(Settings workspace → Activity Log Monitor)",
          export: "(Activity Log Monitor — Export Excel)",
        },
        flowGroup: "Settings tools",
      },
      {
        key: "notification_emails",
        label: "Notification emails",
        description:
          "Configure product and custom notification emails, templates, and audiences.",
        actions: ["view", "edit"],
        actionLabels: {
          view: "View Notification emails",
          edit: "Edit notification settings and templates",
        },
        actionWhere: {
          view: "(Settings workspace → Notification emails)",
          edit: "(Notification emails — Save / Create alert / Send test)",
        },
        flowGroup: "Settings tools",
      },
    ],
  },
];

const ESS_CATALOG_SOURCE = [
  ESS_QUICK_PANEL_MODULE_RAW,
  PROFILE_MODULE_RAW,
  HOME_MODULE_RAW,
  ESS_ATTENDANCE_MODULE_RAW,
  ESS_PAYROLL_MODULE_RAW,
  ESS_REPORTS_MODULE_RAW,
  ESS_ASSETS_MODULE_RAW,
  MY_TEAM_MODULE_RAW,
];

export const ADMIN_RBAC_CATALOG = attachStrictMeta(ADMIN_CATALOG_SOURCE);
export const ESS_SYSTEM_RBAC_CATALOG = attachStrictMeta(ESS_CATALOG_SOURCE);

/** Admin (custom-role) catalog: top-nav modules only. Alias kept for backward compatibility. */
export const CATALOG = ADMIN_RBAC_CATALOG;

/**
 * Screen Access wizard + stored JSON shape for roles: system → ESS shell catalog;
 * custom → admin app catalog.
 */
export function getScreenCatalogForRole(role) {
  if (role && String(role.type || "").toLowerCase() === "system") {
    return ESS_SYSTEM_RBAC_CATALOG;
  }
  return ADMIN_RBAC_CATALOG;
}

/**
 * Group screens that share the same `flowGroup` for Screen Access.
 * Same label always merges into one section (even if non-adjacent in the
 * array) so React keys stay unique and the UI never shows duplicate headers.
 * Screens without flowGroup each form their own anonymous group.
 * Group order = first appearance of each label; screen order within a group
 * follows catalog/flow array order — never sort alphabetically.
 */
export function groupScreensForAccess(screens = []) {
  const groups = [];
  const labeled = new Map();
  for (const screen of screens) {
    const label = screen?.flowGroup ? String(screen.flowGroup).trim() : "";
    if (!label) {
      groups.push({ label: "", screens: [screen] });
      continue;
    }
    const existing = labeled.get(label);
    if (existing) {
      existing.screens.push(screen);
    } else {
      const group = { label, screens: [screen] };
      labeled.set(label, group);
      groups.push(group);
    }
  }
  return groups;
}

// Must mirror Sidebar.jsx nav tab order. Includes `home`; Home is not in CATALOG.
export const TOP_NAV_MODULE_KEYS = [
  "home",
  "configuration",
  "employee",
  "attendance",
  "payroll",
  "hiring",
  "assets",
  "communication",
  "helpdesk",
  "user_roles",
  "workspaces",
];

const uniq = (arr) => [...new Set(arr)];

export const validateCatalogConsistency = ({
  catalog = ADMIN_RBAC_CATALOG,
  actionKeys = ACTIONS,
  topNavModuleKeys = TOP_NAV_MODULE_KEYS,
  requireTopNavAlignment = true,
} = {}) => {
  const issues = [];
  const moduleKeys = catalog.map((m) => m.key);
  const duplicateModules = moduleKeys.filter(
    (key, idx) => moduleKeys.indexOf(key) !== idx
  );
  if (duplicateModules.length) {
    issues.push(`Duplicate module keys: ${uniq(duplicateModules).join(", ")}`);
  }

  for (const mod of catalog) {
    const moduleActions = Array.isArray(mod.actions) ? mod.actions : [];
    const invalidModuleActions = moduleActions.filter(
      (a) => !actionKeys.includes(a)
    );
    if (invalidModuleActions.length) {
      issues.push(
        `Module "${mod.key}" has invalid actions: ${invalidModuleActions.join(", ")}`
      );
    }

    if (!Array.isArray(mod.screens) || mod.screens.length === 0) {
      issues.push(`Module "${mod.key}" has no screens configured.`);
      continue;
    }

    const screenKeys = mod.screens.map((s) => s.key);
    const duplicateScreenKeys = screenKeys.filter(
      (key, idx) => screenKeys.indexOf(key) !== idx
    );
    if (duplicateScreenKeys.length) {
      issues.push(
        `Module "${mod.key}" has duplicate screen keys: ${uniq(
          duplicateScreenKeys
        ).join(", ")}`
      );
    }

    for (const screen of mod.screens) {
      const screenActions = Array.isArray(screen.actions) ? screen.actions : [];
      if (!screenActions.includes("view")) {
        issues.push(`Screen "${mod.key}.${screen.key}" is missing required "view" action.`);
      }
      const invalidScreenActions = screenActions.filter(
        (a) => !actionKeys.includes(a)
      );
      if (invalidScreenActions.length) {
        issues.push(
          `Screen "${mod.key}.${screen.key}" has invalid actions: ${invalidScreenActions.join(", ")}`
        );
      }
      const outOfModuleActions = screenActions.filter(
        (a) => !moduleActions.includes(a)
      );
      if (outOfModuleActions.length) {
        issues.push(
          `Screen "${mod.key}.${screen.key}" uses actions not declared at module level: ${outOfModuleActions.join(", ")}`
        );
      }
      if (!screen.description || !String(screen.description).trim()) {
        issues.push(`Screen "${mod.key}.${screen.key}" is missing description.`);
      }
      if (screen.actionLabels && typeof screen.actionLabels === "object") {
        const strayLabelKeys = Object.keys(screen.actionLabels).filter(
          (k) => !screenActions.includes(k)
        );
        if (strayLabelKeys.length) {
          issues.push(
            `Screen "${mod.key}.${screen.key}" has actionLabels for undeclared actions: ${strayLabelKeys.join(", ")}`
          );
        }
      }
      if (screen.actionWhere && typeof screen.actionWhere === "object") {
        const strayWhereKeys = Object.keys(screen.actionWhere).filter(
          (k) => !screenActions.includes(k)
        );
        if (strayWhereKeys.length) {
          issues.push(
            `Screen "${mod.key}.${screen.key}" has actionWhere for undeclared actions: ${strayWhereKeys.join(", ")}`
          );
        }
      }
    }
  }

  if (requireTopNavAlignment) {
    const missingInCatalog = topNavModuleKeys
      .filter((k) => k !== "home")
      .filter((k) => !moduleKeys.includes(k));
    if (missingInCatalog.length) {
      issues.push(
        `Top-nav modules missing in catalog: ${missingInCatalog.join(", ")}`
      );
    }
  }

  return issues;
};

const adminCatalogIssues = validateCatalogConsistency({
  catalog: ADMIN_RBAC_CATALOG,
  requireTopNavAlignment: true,
});
const essCatalogIssues = validateCatalogConsistency({
  catalog: ESS_SYSTEM_RBAC_CATALOG,
  requireTopNavAlignment: false,
});

export const CATALOG_VALIDATION_ISSUES = [...adminCatalogIssues, ...essCatalogIssues];

if (typeof window !== "undefined" && CATALOG_VALIDATION_ISSUES.length > 0) {
  console.warn("[RBAC] Catalog validation issues:", CATALOG_VALIDATION_ISSUES);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const MODULE_KEYS = ADMIN_RBAC_CATALOG.map((m) => m.key);

export const ESS_MODULE_KEYS = ESS_SYSTEM_RBAC_CATALOG.map((m) => m.key);

export const getModule = (moduleKey, catalog = ADMIN_RBAC_CATALOG) =>
  catalog.find((m) => m.key === moduleKey) || null;

export const getScreen = (moduleKey, screenKey, catalog = ADMIN_RBAC_CATALOG) => {
  const mod = getModule(moduleKey, catalog);
  if (!mod) return null;
  return mod.screens.find((s) => s.key === screenKey) || null;
};

// UI-friendly label for an action on a specific screen. Falls back to Title
// Case of the action key if the screen did not declare a custom label.
export const getActionLabel = (moduleKey, screenKey, action, catalog = ADMIN_RBAC_CATALOG) => {
  if (!action) return "";
  const screen = getScreen(moduleKey, screenKey, catalog);
  const label = screen?.actionLabels?.[action];
  return label && String(label).trim() ? label : toTitleCase(action);
};

// Returns a fresh "all false" permission shape matching the catalog. Useful
// when seeding a blank role or filling in missing keys at runtime.
export const buildEmptyAccess = (catalog = ADMIN_RBAC_CATALOG) => {
  const out = {};
  for (const mod of catalog) {
    const moduleAcc = {};
    for (const action of ACTIONS) moduleAcc[action] = false;
    const screens = {};
    for (const screen of mod.screens) {
      const screenAcc = {};
      for (const action of ACTIONS) screenAcc[action] = false;
      screens[screen.key] = screenAcc;
    }
    moduleAcc.screens = screens;
    out[mod.key] = moduleAcc;
  }
  return out;
};

/** All permissions true for every declared action (used when seeding default ESS roles). */
export const buildFullAccess = (catalog = ESS_SYSTEM_RBAC_CATALOG) => {
  const out = {};
  for (const mod of catalog) {
    const moduleAcc = { screens: {} };
    for (const action of ACTIONS) moduleAcc[action] = false;
    for (const screen of mod.screens) {
      const screenAcc = {};
      for (const action of ACTIONS) screenAcc[action] = false;
      for (const a of screen.actions || []) {
        if (ACTIONS.includes(a)) screenAcc[a] = true;
      }
      moduleAcc.screens[screen.key] = normalizeScreenPermissionRow(
        screenAcc,
        screen.actions
      );
    }
    syncModuleFlagsFromScreens(moduleAcc, mod);
    out[mod.key] = moduleAcc;
  }
  return out;
};

/**
 * True when stored access predates the current ESS module set (e.g. missing
 * `home`). Left-nav visibility is derived from profile / attendance / payroll /
 * reports / my_team — not a separate `ess_left_nav` module.
 */
export const isLegacyEssScreenAccess = (raw) => {
  if (!raw || typeof raw !== "object") return true;
  const home = raw.home ?? raw.ess_home;
  return !home || typeof home !== "object";
};

/**
 * Default ESS screenAccess for seeded system roles. EMPLOYEE: full ESS except My Team;
 * MANAGER: full ESS including My Team.
 */
export const buildDefaultEssSystemRoleAccess = (roleCode) => {
  const full = buildFullAccess(ESS_SYSTEM_RBAC_CATALOG);
  const code = String(roleCode || "").toUpperCase();
  if (code !== "EMPLOYEE") return full;
  const teamMod = full.my_team;
  if (teamMod?.screens) {
    for (const screenKey of Object.keys(teamMod.screens)) {
      for (const a of ACTIONS) teamMod.screens[screenKey][a] = false;
    }
    const teamDef = ESS_SYSTEM_RBAC_CATALOG.find((m) => m.key === "my_team");
    if (teamDef) syncModuleFlagsFromScreens(teamMod, teamDef);
  }
  return full;
};

// Merges an arbitrary saved shape onto the canonical shape so older/partial
// rows still work (defensive read). Never mutates input.
//
// expandLegacyActions: when true (default), seed missing Action/Settings screens
// from coarse parents for pre-migration rows. Employee custom-role snapshots must
// pass false — otherwise revoking Action screens (keys stripped on save) gets
// re-seeded on the next login and the URM edit appears not to apply.
/** Attendance Process pipeline screens (Reconciliation → Finalise). */
export const ATTENDANCE_PROCESS_SCREEN_KEYS = [
  "att_proc_reconciliation",
  "att_proc_process",
  "att_proc_checklist",
  "att_proc_preview_validate",
  "att_proc_finalise",
];

/**
 * Seeds Attendance process-step screens from process_attendance hub grants
 * only when no att_proc_* rows exist yet (hub-only legacy roles).
 * If any process step was saved in URM, leave the matrix as-is (payslip-only / selective).
 */
export function expandAttendanceProcessScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const att = raw.attendance;
  if (!att || typeof att !== "object") return raw;
  const screens =
    att.screens && typeof att.screens === "object" ? { ...att.screens } : {};
  if (ATTENDANCE_PROCESS_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }
  const process = screens.process_attendance || {};
  if (!process.view && !process.edit && !process.approve && !process.export) {
    return raw;
  }
  screens.att_proc_reconciliation = actionRow({
    view: !!process.view || !!process.edit,
    edit: !!process.edit,
  });
  screens.att_proc_process = actionRow({
    view: !!process.view || !!process.edit,
    edit: !!process.edit,
  });
  screens.att_proc_checklist = actionRow({
    view: !!process.view || !!process.edit,
    edit: !!process.edit,
  });
  screens.att_proc_preview_validate = actionRow({
    view: !!process.view || !!process.edit,
    edit: !!process.edit,
  });
  screens.att_proc_finalise = actionRow({
    view: !!process.view || !!process.approve,
    approve: !!process.approve || !!process.edit,
  });
  return { ...raw, attendance: { ...att, screens } };
}

/** Payroll Run Payroll pipeline step screens. */
export const PAYROLL_PROCESS_SCREEN_KEYS = [
  "pay_proc_new_joinees",
  "pay_proc_left_employees",
  "pay_proc_salary_revisions",
  "pay_proc_attendance_validation",
  "pay_proc_lop_reversal_arrears",
  "pay_proc_one_time_inputs",
  "pay_proc_bank_details",
  "pay_proc_headcount_reconcile",
  "pay_proc_process",
  "pay_proc_checklist",
  "pay_proc_disbursement",
  "pay_proc_preview_validate",
  "pay_proc_finalise",
  "pay_proc_payslips",
];

/**
 * Seeds Payroll process-step screens from run_payroll / process_payroll hubs
 * only when no pay_proc_* rows exist yet. Selective URM grants stay selective.
 */
export function expandPayrollProcessScreens(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const pay = raw.payroll;
  if (!pay || typeof pay !== "object") return raw;
  const screens =
    pay.screens && typeof pay.screens === "object" ? { ...pay.screens } : {};
  if (PAYROLL_PROCESS_SCREEN_KEYS.some((k) => screens[k] != null)) {
    return raw;
  }
  const run = screens.run_payroll || {};
  const process = screens.process_payroll || {};
  const slips = screens.payslips || {};
  const hubView = !!(run.view || process.view);
  const hubEdit = !!(run.create || run.edit || process.create || process.edit);
  const hubApprove = !!(run.approve || process.approve || slips.approve);
  if (!hubView && !hubEdit && !hubApprove) return raw;

  const viewEdit = actionRow({ view: hubView || hubEdit, edit: hubEdit });
  for (const key of [
    "pay_proc_new_joinees",
    "pay_proc_left_employees",
    "pay_proc_salary_revisions",
    "pay_proc_attendance_validation",
    "pay_proc_lop_reversal_arrears",
    "pay_proc_one_time_inputs",
    "pay_proc_bank_details",
    "pay_proc_headcount_reconcile",
    "pay_proc_checklist",
    "pay_proc_disbursement",
    "pay_proc_preview_validate",
  ]) {
    screens[key] = { ...viewEdit };
  }
  screens.pay_proc_process = actionRow({
    view: hubView || hubEdit,
    edit: hubEdit,
  });
  screens.pay_proc_finalise = actionRow({
    view: hubView || hubApprove,
    approve: hubApprove || hubEdit,
  });
  screens.pay_proc_payslips = actionRow({
    view: !!slips.view || hubView,
    edit: !!slips.edit || hubEdit,
    export: !!slips.export,
    approve: !!slips.approve || hubApprove,
  });
  return { ...raw, payroll: { ...pay, screens } };
}

export const normalizeAccess = (
  raw,
  catalog = ADMIN_RBAC_CATALOG,
  { expandLegacyActions = true } = {}
) => {
  const base = buildEmptyAccess(catalog);
  if (!raw || typeof raw !== "object") return base;

  // Action / Settings expand is only for system EMPLOYEE/MANAGER (revoke must stick).
  // Process pipeline expand always runs: hub-only grants (process_attendance / run_payroll)
  // must seed att_proc_* / pay_proc_* for custom roles too.
  const withActions = expandLegacyActions
    ? expandPayrollReimbControlScreens(
        expandAttendanceSettingsScreens(
          expandEmployeeProfileScreens(
            expandEmployeeActionScreens(
              expandPayrollActionScreens(expandAttendanceActionScreens(raw))
            )
          )
        )
      )
    : raw;
  const source = expandPayrollProcessScreens(expandAttendanceProcessScreens(withActions));

  for (const mod of catalog) {
    const rawMod = source[mod.key];
    if (!rawMod || typeof rawMod !== "object") continue;

    for (const action of ACTIONS) {
      if (action !== "view") continue;
      if (rawMod[action] === true) base[mod.key][action] = true;
    }

    const rawScreens =
      rawMod.screens && typeof rawMod.screens === "object"
        ? rawMod.screens
        : null;

    if (rawScreens) {
      for (const screen of mod.screens) {
        const rawScr = rawScreens[screen.key];
        if (!rawScr || typeof rawScr !== "object") continue;
        for (const action of ACTIONS) {
          if (rawScr[action] === true) {
            base[mod.key].screens[screen.key][action] = true;
          }
        }
      }
    }

    for (const screen of mod.screens) {
      const rawScrForScreen =
        rawScreens && typeof rawScreens === "object" ? rawScreens[screen.key] : null;
      const preferViewOffIngest =
        rawScrForScreen &&
        typeof rawScrForScreen === "object" &&
        rawScrForScreen.view === false;
      base[mod.key].screens[screen.key] = normalizeScreenPermissionRow(
        base[mod.key].screens[screen.key],
        screen.actions,
        preferViewOffIngest ? { preferViewOff: true } : {}
      );
    }

    syncModuleFlagsFromScreens(base[mod.key], mod);

    for (const action of ACTIONS) {
      if (action !== "view") continue;
      if (rawMod[action] === true) base[mod.key][action] = true;
    }
    if (rawMod.enabled === false) base[mod.key].enabled = false;
  }
  return base;
};
